import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import VenueMap from "@/components/conference/VenueMap";
import LiveRegistrationSummary from "@/components/conference/LiveRegistrationSummary";
import LiveStreamCallout from "@/components/conference/LiveStreamCallout";
import { RegistrationInfoRibbon } from "@/components/conference/RegistrationInfoRibbon";
import CountdownTimer from "@/components/CountdownTimer";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import {
  CheckCircle2,
  Heart,
  Plane,
  Mail,
  Loader2,
  Sparkles,
  Mic,
  MapPin,
  ClipboardList,
  CalendarDays,
  Music,
  ImageIcon,
  ArrowLeft,
  Plus,
  Trash2,
  Users,
  XCircle,
  MessageSquareHeart,
  Upload,
  Info,
  Phone,
  ArrowRight,
  Download,
  CalendarClock,
  HandHeart,
} from "lucide-react";
const CancelRegistrationPanel = lazy(() => import("@/components/conference/CancelRegistrationPanel"));
const ConferenceFeedbackPanel = lazy(() => import("@/components/conference/ConferenceFeedbackPanel"));
const PaymentConfirmationPanel = lazy(() => import("@/components/conference/PaymentConfirmationPanel"));
const SouthernProgramSchedule = lazy(() => import("@/components/conference/SouthernProgramSchedule"));
const MidwestProgramSchedule = lazy(() => import("@/components/conference/MidwestProgramSchedule"));
const ThemeSongCard = lazy(() => import("@/components/conference/ThemeSongCard"));
const WorshipTabPanel = lazy(() => import("@/components/conference/WorshipTabPanel"));
import ofuesiLogo from "@/assets/ofuesi-logo.png";
import zelleQR from "@/assets/payments/zelle-qr.png";
import RegistrationQR, { buildRegistrationUrl } from "@/components/RegistrationQR";

import { US_STATES } from "@/lib/constants";
import {
  CONFERENCE_2026_SCRIPTURE,
  CONFERENCE_2026_THEME,
  CONFERENCE_2026_TOPIC_POINTS,
  PublicConferenceRow,
  PublicKitEventRow,
  ResolvedPublicConference,
  formatConferenceDateRange,
  resolvePublicConference,
} from "@/lib/conferencePublic";

interface ContentRow {
  id: string;
  section: "theme" | "speakers" | "venue" | "food" | "program" | "worship" | "flyer" | "directions";
  region_id: string | null;
  title: string | null;
  subtitle: string | null;
  body: string | null;
  data: any;
  order_index: number;
}
interface RegionRow { id: string; name: string; states?: string[] | null; conference_start_date?: string | null; coordinator_name?: string | null; coordinator_email?: string | null; coordinator_phone?: string | null }

const REGIONS = [
  "Eastern Region",
  "Mid West Region",
  "North West Region",
  "Southwest Region",
  "Southern Region",
  "South East Region",
];

const COUNTRY_CODES = ["+1", "+44", "+91", "+61", "+27", "+233", "+234", "+254"];

// Payment details (sourced from the Give page)
const ZELLE_RECIPIENT = "ofuesi.treasurer@gmail.com";

type AgeGroup = "adult" | "teen" | "child_6_12" | "child_0_5";
type Relationship = "spouse" | "child" | "other_adult";
type ChildCategory = "child_0_5" | "child_6_12" | "teen" | "adult_18_plus";

interface RosterMember {
  id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  age_group: AgeGroup;
  relationship: Relationship;
  age?: number;
  child_category?: ChildCategory;
  gender?: string;
  notes?: string;
  email?: string;
  country_code?: string;
  mobile?: string;
  attendee_type?: "adult" | "student";
}

const childCategoryRange: Record<ChildCategory, [number, number]> = {
  child_0_5: [0, 5],
  child_6_12: [6, 12],
  teen: [13, 17],
  adult_18_plus: [18, 120],
};
const ageGroupFromChildCategory = (c: ChildCategory): AgeGroup =>
  c === "adult_18_plus" ? "adult" : c === "teen" ? "teen" : c === "child_6_12" ? "child_6_12" : "child_0_5";

const memberSchema = z.object({
  id: z.string(),
  first_name: z.string().trim().min(1, "First name required").max(80),
  middle_name: z.string().trim().max(80).optional(),
  last_name: z.string().trim().min(1, "Last name required").max(80),
  age_group: z.enum(["adult", "teen", "child_6_12", "child_0_5"]),
  relationship: z.enum(["spouse", "child", "other_adult"]),
  age: z.number().int().min(0).max(120).optional(),
  child_category: z.enum(["child_0_5", "child_6_12", "teen", "adult_18_plus"]).optional(),
  gender: z.string().max(40).optional(),
  notes: z.string().max(500).optional(),
  email: z.string().trim().max(200).optional().or(z.literal("")),
  country_code: z.string().max(6).optional().or(z.literal("")),
  mobile: z.string().trim().max(20).optional().or(z.literal("")),
  attendee_type: z.enum(["adult", "student"]).optional(),
  needs_scholarship: z.boolean().optional(),
});

const schema = z.object({
  category: z.enum(["individual", "couple", "family"]),
  firstName: z.string().trim().min(1, "First name is required").max(80),
  middleName: z.string().trim().max(80).optional().or(z.literal("")),
  lastName: z.string().trim().min(1, "Last name is required").max(80),
  countryCode: z.string().min(1),
  mobile: z.string().trim().min(7, "Enter a valid mobile number").max(20)
    .regex(/^[\d\s\-()]+$/, "Numbers only"),
  email: z.string().trim().email("Enter a valid email").max(200),
  state: z.string().min(1, "Please select your state"),
  region: z.string().min(1, "Please select your region"),
  city: z.string().trim().min(1, "City is required").max(100),
  address: z.string().trim().min(5, "Mailing address is required").max(500),
  includeSpouse: z.boolean(),
  spouseFirst: z.string().trim().max(80).optional().or(z.literal("")),
  spouseMiddle: z.string().trim().max(80).optional().or(z.literal("")),
  spouseLast: z.string().trim().max(80).optional().or(z.literal("")),
  spouseCountryCode: z.string().optional().or(z.literal("")),
  spouseMobile: z.string().trim().max(20).optional().or(z.literal(""))
    .refine((v) => !v || /^[\d\s\-()]+$/.test(v), "Numbers only"),
  spouseEmail: z.string().trim().max(200).optional().or(z.literal(""))
    .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "Enter a valid email"),
  boardingType: z.enum(["camp", "hotel"]).optional(),
  children: z.array(memberSchema),
  otherAdults: z.array(memberSchema),
  isStudent: z.boolean().optional(),
  university: z.string().trim().max(200).optional().or(z.literal("")),
  needsScholarship: z.boolean().optional(),
  spouseNeedsScholarship: z.boolean().optional(),
  generalComments: z.string().max(2000).optional().or(z.literal("")),
  dietary: z.string().max(1000).optional().or(z.literal("")),
}).superRefine((v, ctx) => {
  if ((v.category === "family" || v.category === "couple") && v.includeSpouse) {
    if (!v.spouseFirst?.trim()) ctx.addIssue({ code: "custom", path: ["spouseFirst"], message: "Spouse first name required" });
    if (!v.spouseLast?.trim()) ctx.addIssue({ code: "custom", path: ["spouseLast"], message: "Spouse last name required" });
  }
  if (v.category === "couple" && v.children.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["children"],
      message: "Couple registration cannot include children. Please choose Family if you have children.",
    });
  }
  if (v.category === "family") {
    v.children.forEach((c, idx) => {
      if (!c.first_name?.trim()) ctx.addIssue({ code: "custom", path: ["children", idx, "first_name"], message: `Child #${idx + 1}: first name is required` });
      if (!c.last_name?.trim()) ctx.addIssue({ code: "custom", path: ["children", idx, "last_name"], message: `Child #${idx + 1}: last name is required` });
      if (c.age === undefined || c.age === null || Number.isNaN(c.age as number)) {
        ctx.addIssue({ code: "custom", path: ["children", idx, "age"], message: `Child #${idx + 1}: please enter the attendee's actual age.` });
      }
    });
    v.otherAdults.forEach((a, idx) => {
      if (!a.first_name?.trim()) ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "first_name"], message: `Adult #${idx + 1}: first name is required` });
      if (!a.last_name?.trim()) ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "last_name"], message: `Adult #${idx + 1}: last name is required` });
    });
  }
  if (v.category === "individual" && v.isStudent && !(v.university && v.university.trim())) {
    ctx.addIssue({ code: "custom", path: ["university"], message: "Please tell us which university you attend" });
  }
  if (v.category === "individual") {
    v.otherAdults.forEach((a, idx) => {
      if (!a.first_name?.trim() || !a.last_name?.trim()) {
        ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "first_name"], message: `Individual #${idx + 2}: full name is required` });
      }
      if (!a.mobile?.trim()) {
        ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "mobile"], message: `Individual #${idx + 2}: mobile number is required` });
      } else if (!/^[\d\s\-()]+$/.test(a.mobile)) {
        ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "mobile"], message: `Individual #${idx + 2}: mobile must be digits only` });
      }
      if (!a.email?.trim()) {
        ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "email"], message: `Individual #${idx + 2}: email is required` });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) {
        ctx.addIssue({ code: "custom", path: ["otherAdults", idx, "email"], message: `Individual #${idx + 2}: enter a valid email` });
      }
    });
  }
});


type FormState = z.infer<typeof schema>;

const newId = () => Math.random().toString(36).slice(2, 10);

const VALID_TABS = ["details", "register", "schedule", "worship", "volunteer", "payment", "cancel", "feedback"] as const;
type TabValue = typeof VALID_TABS[number];

// Category metadata: color theme + label. Pricing for Family is dynamic — see computeSouthernPrice.
const CATEGORY_META: Record<"individual" | "couple" | "family", {
  label: string;
  description: string;
  feeHint: string;
  selectedRing: string;
  selectedBg: string;
  selectedBorder: string;
  accent: string;
  badge: string;
}> = {
  individual: {
    label: "Individual",
    description: "Register yourself or one or more individual adults/students under a single registration and payment.",
    feeHint: "",
    selectedRing: "ring-2 ring-blue-300",
    selectedBg: "bg-gradient-to-br from-blue-50 to-sky-50",
    selectedBorder: "border-blue-400",
    accent: "text-blue-700",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
  },
  couple: {
    label: "Couple",
    description: "Register as a couple (spouses attending together).",
    feeHint: "",
    selectedRing: "ring-2 ring-amber-300",
    selectedBg: "bg-gradient-to-br from-amber-50 to-yellow-50",
    selectedBorder: "border-amber-400",
    accent: "text-amber-700",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
  },
  family: {
    label: "Family",
    description: "Designed for families registering together. Final fee is calculated dynamically based on family composition and selected attendees.",
    feeHint: "",
    selectedRing: "ring-2 ring-emerald-300",
    selectedBg: "bg-gradient-to-br from-emerald-50 to-green-50",
    selectedBorder: "border-emerald-400",
    accent: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
};

// Estimated starting prices shown on category cards (Southern region only).
// This is a hint — the live Registration Fee Summary computes the actual total.
const SOUTHERN_STARTING_PRICE: Record<"individual" | "couple" | "family", number> = {
  individual: 125,
  couple: 250,
  family: 350,
};

// Family pricing matrix per spec:
//  Husband+Wife + only 0–5 children     = $250
//  Husband+Wife + any child > 5         = $350
//  Single parent + only 0–5 children    = $125
//  Single parent + 1 child > 5          = $250
//  Single parent + 2+ children > 5      = $350
//  Each additional adult (18+) beyond primary contact + spouse: +$125
const ADDITIONAL_ADULT_FEE = 125;
// Individual attendee pricing (per-person). Configurable so a future student
// discount only requires flipping this constant — no UI/reporting changes.
const INDIVIDUAL_FEES: Record<"adult" | "student", number> = {
  adult: 125,
  student: 125,
};
const attendeeFee = (t?: "adult" | "student") => INDIVIDUAL_FEES[t === "student" ? "student" : "adult"];
function computeSouthernBasePrice(
  category: "individual" | "couple" | "family",
  hasSpouse: boolean,
  children: RosterMember[],
  otherAdults: RosterMember[] = [],
  primaryAttendeeType: "adult" | "student" = "adult",
): { price: number; familyType: string; familyBase: number; additionalAdults: number; additionalAdultsFee: number } {
  if (category === "individual") {
    const primaryFee = attendeeFee(primaryAttendeeType);
    const extrasFee = (otherAdults || []).reduce((s, a) => s + attendeeFee(a.attendee_type), 0);
    const extras = (otherAdults || []).length;
    const price = primaryFee + extrasFee;
    return { price, familyType: extras > 0 ? "individual_group" : "individual", familyBase: primaryFee, additionalAdults: extras, additionalAdultsFee: extrasFee };
  }
  if (category === "couple") {
    const price = hasSpouse ? 250 : 125;
    return { price, familyType: hasSpouse ? "couple_no_kids" : "individual", familyBase: price, additionalAdults: 0, additionalAdultsFee: 0 };
  }
  // family
  // Children whose category is "adult_18_plus" (or actual age >= 18) are NOT treated
  // as children for the family base tier — they are billed as additional adults at $125 each.
  const isAdultDependent = (c: RosterMember) =>
    c.child_category === "adult_18_plus" || (c.age ?? -1) >= 18;
  const adultDependents = children.filter(isAdultDependent);
  const minorChildren = children.filter((c) => !isAdultDependent(c));
  const olderCount = minorChildren.filter(
    (c) => (c.age ?? (c.child_category ? childCategoryRange[c.child_category][0] : 0)) > 5,
  ).length;
  let familyBase = 0;
  let familyType = "";
  if (hasSpouse) {
    familyBase = olderCount > 0 ? 350 : 250;
    familyType = olderCount > 0 ? "couple_with_older_kids" : "couple_with_kids_under_5";
  } else if (olderCount >= 2) {
    familyBase = 350;
    familyType = "single_parent_with_multiple_older_kids";
  } else if (olderCount === 1) {
    familyBase = 250;
    familyType = "single_parent_with_older_kid";
  } else {
    familyBase = 125;
    familyType = "single_parent_with_kids_under_5";
  }
  const additionalAdults = (otherAdults || []).length + adultDependents.length;
  const additionalAdultsFee = additionalAdults * ADDITIONAL_ADULT_FEE;
  return {
    price: familyBase + additionalAdultsFee,
    familyType,
    familyBase,
    additionalAdults,
    additionalAdultsFee,
  };
}


const HOTEL_GUIDE_APP_VIEW_URL = "/hotel-accommodation-guide";


const ConferenceRegistration = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const normalizeSlug = (s: string | null | undefined) =>
    (s || "")
      .toLowerCase()
      // strip optional 4-digit year suffix like "-2026" or "2026"
      .replace(/[-_ ]?\d{4}$/, "")
      .replace(/-?registration$/, "")
      .replace(/region/g, "")
      .replace(/[^a-z0-9]/g, "")
      .trim();
  const tabFromUrl = searchParams.get("tab") as TabValue | null;
  const initialTab: TabValue =
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "details";
  const [tab, setTabState] = useState<TabValue>(initialTab);
  const setTab = useCallback(
    (next: string) => {
      const v = (VALID_TABS.includes(next as TabValue) ? next : "details") as TabValue;
      setTabState(v);
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set("tab", v);
          return sp;
        },
        { replace: false }
      );
      // Scroll the tab strip to the top of the viewport so selected content is above the fold
      requestAnimationFrame(() => {
        const el = document.getElementById("conf-tabs");
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 8;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      });
    },
    [setSearchParams]
  );
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState<ContentRow[]>([]);
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [publicConferences, setPublicConferences] = useState<PublicConferenceRow[]>([]);
  const [kitEvents, setKitEvents] = useState<PublicKitEventRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const hasRouteRegion = Boolean(
    routeSlug ||
      searchParams.get("region") ||
      (typeof window !== "undefined" &&
        /^\/[a-z-]+-registration\/?$/i.test(window.location.pathname))
  );
  const [form, setForm] = useState<FormState>({
    category: "family",
    firstName: "", middleName: "", lastName: "",
    countryCode: "+1", mobile: "", email: "",
    state: "", region: "", city: "", address: "",
    includeSpouse: false, spouseFirst: "", spouseMiddle: "", spouseLast: "", spouseCountryCode: "+1", spouseMobile: "", spouseEmail: "",
    boardingType: "camp",
    children: [], otherAdults: [],
    isStudent: false, university: "",
    needsScholarship: false, spouseNeedsScholarship: false, generalComments: "",
    dietary: "",
  });
  const [paymentChoice, setPaymentChoice] = useState<"zelle" | "online" | "in_person" | "pay_later">("zelle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; items: string[] }>({ open: false, title: "", items: [] });
  const [adultsConfirmed, setAdultsConfirmed] = useState(false);
  const [adultConfirmOpen, setAdultConfirmOpen] = useState(false);
  const [spouseSuggestDismissed, setSpouseSuggestDismissed] = useState(false);
  


  useEffect(() => {
    const t = searchParams.get("tab") as TabValue | null;
    if (t && VALID_TABS.includes(t) && t !== tab) setTabState(t);
  }, [searchParams, tab]);

  // Capture attribution params from URL once and persist for the session
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const keys = ["src", "utm_source", "utm_medium", "utm_campaign"] as const;
      const existing = JSON.parse(sessionStorage.getItem("ofuesi_attrib") || "{}");
      const next: Record<string, string> = { ...existing };
      let changed = false;
      keys.forEach(k => {
        const v = sp.get(k);
        if (v && !next[k]) { next[k] = v; changed = true; }
      });
      if (!next.referrer && document.referrer) { next.referrer = document.referrer; changed = true; }
      if (changed) sessionStorage.setItem("ofuesi_attrib", JSON.stringify(next));
    } catch {}
  }, []);

  useEffect(() => {
    document.title = "OFUESI Regional Conference 2026 — Registration";
    const desc =
      "Register for the OFUESI Regional Conference 2026. Family-friendly registration with travel guidance and Zelle payment.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0): Promise<void> => {
      try {
        void (supabase.from("conference_content" as any) as any)
          .select("*")
          .eq("conference_slug", "regional-conference-2026")
          .eq("is_active", true)
          .order("order_index")
          .then(({ data, error }: { data: ContentRow[] | null; error: any }) => {
            if (error) throw error;
            if (!cancelled) setContent(data || []);
          })
          .catch((err: any) => {
            console.warn("[ConferenceRegistration] content load failed:", err);
          });

        const [regionsResult, confResult, kitResult] = await Promise.allSettled([
          supabase.from("regions").select("id,name,states,coordinator_name,coordinator_email,coordinator_phone").order("name"),
          supabase
            .from("conferences")
            .select("id, region_id, conference_name, conference_date, conference_dates, location, flyer_url, is_active, status, slogan, description, facilitators, contact_phone, contact_name")
            .eq("is_active", true)
            .eq("status", "upcoming")
            .ilike("conference_name", "%2026%"),
          supabase
            .from("conference_kit_events")
            .select("region_id, venue_name, venue_address, city, state, start_date, end_date, conference_year, flyer_url, flyer_approved, status, coordinator_phone, hotel_guide_filename, hotel_guide_size, hotel_guide_uploaded_at")
            .eq("conference_year", 2026),
        ]);
        if (cancelled) return;
        if (regionsResult.status === "rejected") throw regionsResult.reason;
        const regionList = (regionsResult.value.data as RegionRow[]) || [];
        setRegions(regionList);
        setPublicConferences(
          confResult.status === "fulfilled" ? ((confResult.value.data as PublicConferenceRow[]) || []) : []
        );
        setKitEvents(
          kitResult.status === "fulfilled" ? ((kitResult.value.data as PublicKitEventRow[]) || []) : []
        );
        if (confResult.status === "rejected") {
          console.warn("[ConferenceRegistration] conference load failed:", confResult.reason);
        }
        if (kitResult.status === "rejected") {
          console.warn("[ConferenceRegistration] kit-event load failed:", kitResult.reason);
        }
        const qRegion = searchParams.get("region");
        const pathMatch =
          typeof window !== "undefined"
            ? window.location.pathname.match(/^\/([a-z-]+)-registration\/?$/i)
            : null;
        const pathSlug = pathMatch ? pathMatch[1] : null;
        const effectiveSlug = routeSlug || pathSlug;
        let resolvedId: string | null = null;
        if (qRegion && regionList.some((rg) => rg.id === qRegion)) {
          resolvedId = qRegion;
        } else if (effectiveSlug) {
          const target = regionList.find(
            (rg) => normalizeSlug(rg.name) === normalizeSlug(effectiveSlug)
          );
          if (target) resolvedId = target.id;
        }
        if (resolvedId) {
          setSelectedRegionId(resolvedId);
          const regionMatch = regionList.find((rg) => rg.id === resolvedId);
          if (regionMatch) {
            setForm((p) => (p.region ? p : { ...p, region: regionMatch.name }));
          }
          setShowForm(true);
        } else {
          setSelectedRegionId(null);
          setShowForm(false);
        }
        setDataLoading(false);
      } catch (err) {
        console.warn(`[ConferenceRegistration] load attempt ${attempt + 1} failed:`, err);
        if (cancelled) return;
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, [600, 1500, 3000][attempt] || 3000));
          if (!cancelled) return load(attempt + 1);
        }
        setDataLoading(false);
      }
    };
    setDataLoading(true);
    load();
    return () => {
      cancelled = true;
    };
  }, [searchParams, routeSlug]);



  const themeRow = content.find((c) => c.section === "theme");
  const speakerRows = content.filter((c) => c.section === "speakers");
  const worshipRowsCommon = content.filter((c) => c.section === "worship" && !c.region_id);
  const flyerByRegion = (rid: string) => content.find((c) => c.section === "flyer" && c.region_id === rid);
  const venuesByRegion = (rid: string) => content.filter((c) => c.section === "venue" && c.region_id === rid);
  
  const programByRegion = (rid: string) => content.filter((c) => c.section === "program" && c.region_id === rid);
  const worshipByRegion = (rid: string) => content.filter((c) => c.section === "worship" && c.region_id === rid);
  const selectedRegion = regions.find((r) => r.id === selectedRegionId) || null;
  const selectedConference = useMemo<ResolvedPublicConference | null>(
    () =>
      selectedRegion
        ? resolvePublicConference({ region: selectedRegion, conferences: publicConferences, kitEvents })
        : null,
    [selectedRegion, publicConferences, kitEvents],
  );
  const isMidwest = (!!selectedRegion && /midwest/i.test(selectedRegion.name)) || /midwest/i.test(form.region || "");
  const isSouthern = (!!selectedRegion && /southern/i.test(selectedRegion.name)) || /southern/i.test(form.region || "");
  const isEastern = (!!selectedRegion && /eastern/i.test(selectedRegion.name)) || /eastern/i.test(form.region || "") || /eastern/i.test(routeSlug || "");
  const hotelGuide = useMemo(() => {
    if (!selectedRegion) return null;
    const kit = kitEvents.find((k) =>
      k.region_id === selectedRegion.id &&
      Boolean((k as any).hotel_guide_filename || (k as any).hotel_guide_uploaded_at)
    );
    if (!kit) return null;
    const params = new URLSearchParams({
      regionId: selectedRegion.id,
      year: String((kit.conference_year as number) || 2026),
      documentType: "hotel-guide",
    });
    return {
      viewHref: `${HOTEL_GUIDE_APP_VIEW_URL}?${params.toString()}`,
      downloadHref: `${HOTEL_GUIDE_APP_VIEW_URL}?${params.toString()}&download=1`,
      filename: (kit.hotel_guide_filename as string) || "hotel-accommodation-guide.pdf",
      size: (kit.hotel_guide_size as number) || null,
      uploadedAt: (kit.hotel_guide_uploaded_at as string) || null,
    };
  }, [selectedRegion, kitEvents]);

  const openHotelGuide = useCallback((href: string, mode: "view" | "download") => async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (mode === "view") {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    window.open(href, "_blank", "noopener,noreferrer");
  }, []);

  const coordinatorRegion = useMemo(() => {
    if (form.region) {
      const m = regions.find((r) => r.name.toLowerCase() === form.region.toLowerCase());
      if (m) return m;
    }
    return selectedRegion || null;
  }, [regions, form.region, selectedRegion]);


  // Build full roster including primary + spouse + children + other adults
  const buildRoster = () => {
    const list: {
      first_name: string;
      middle_name: string | null;
      last_name: string;
      relationship: "primary" | "spouse" | "child" | "other_adult";
      age_group: AgeGroup;
      age?: number;
      child_category?: ChildCategory;
      gender?: string;
      notes?: string;
      email?: string | null;
      country_code?: string | null;
      mobile?: string | null;
      attendee_type?: "adult" | "student" | null;
      needs_scholarship?: boolean;
    }[] = [];
    list.push({
      first_name: form.firstName.trim(),
      middle_name: form.middleName?.trim() || null,
      last_name: form.lastName.trim(),
      relationship: "primary",
      age_group: "adult",
      email: form.email?.trim() || null,
      country_code: form.countryCode || null,
      mobile: form.mobile?.trim() || null,
      attendee_type: form.category === "individual" ? (form.isStudent ? "student" : "adult") : null,
      needs_scholarship: !!form.needsScholarship,
    });
    if (form.includeSpouse && form.spouseFirst?.trim() && form.spouseLast?.trim()) {
      list.push({
        first_name: form.spouseFirst.trim(),
        middle_name: form.spouseMiddle?.trim() || null,
        last_name: form.spouseLast.trim(),
        relationship: "spouse",
        age_group: "adult",
        email: form.spouseEmail?.trim() || null,
        country_code: form.spouseCountryCode || null,
        mobile: form.spouseMobile?.trim() || null,
        needs_scholarship: !!form.spouseNeedsScholarship,
      });
    }
    form.children.forEach((c) =>
      list.push({
        first_name: c.first_name.trim(),
        middle_name: c.middle_name?.trim() || null,
        last_name: c.last_name.trim(),
        relationship: "child",
        age_group: c.age_group,
        age: c.age,
        child_category: c.child_category,
        gender: c.gender,
        notes: c.notes,
      })
    );
    // Additional adults/students come from family "other adults" OR from Individual "Additional Attendees"
    const includeAdults = form.category === "family" || form.category === "individual";
    if (includeAdults) {
      form.otherAdults.forEach((a) =>
        list.push({
          first_name: a.first_name.trim(),
          middle_name: a.middle_name?.trim() || null,
          last_name: a.last_name.trim(),
          relationship: "other_adult",
          age_group: "adult",
          gender: a.gender,
          email: a.email?.trim() || null,
          country_code: a.country_code || null,
          mobile: a.mobile?.trim() || null,
          attendee_type: form.category === "individual" ? (a.attendee_type === "student" ? "student" : "adult") : null,
          needs_scholarship: !!a.needs_scholarship,
        })
      );
    }

    return list;
  };


  const roster = useMemo(() => buildRoster(), [form]);
  const counts = useMemo(() => ({
    adults: roster.filter((m) => m.age_group === "adult").length,
    teens: roster.filter((m) => m.age_group === "teen").length,
    c6_12: roster.filter((m) => m.age_group === "child_6_12").length,
    c0_5: roster.filter((m) => m.age_group === "child_0_5").length,
    total: roster.length,
  }), [roster]);
  const total = counts.total;

  const isHotel = isSouthern && form.boardingType === "hotel";
  const familyChildrenForPricing: RosterMember[] = form.category === "family" ? form.children : [];
  const familyAdultsForPricing: RosterMember[] = (form.category === "family" || form.category === "individual") ? form.otherAdults : [];
  const { price: baseCategoryPrice, familyType, familyBase, additionalAdults, additionalAdultsFee } = useMemo(
    () => computeSouthernBasePrice(form.category, form.includeSpouse, familyChildrenForPricing, familyAdultsForPricing, form.isStudent ? "student" : "adult"),
    [form.category, form.includeSpouse, form.children, form.otherAdults, form.isStudent],
  );
  const hotelBillablePersons = useMemo(() => {
    // Count adults & teens from roster, plus spouse-in-intent for couple/family (even before spouse name is typed).
    let n = counts.adults + counts.teens;
    const spouseIntended =
      form.includeSpouse && (form.category === "couple" || form.category === "family");
    const spouseInRoster =
      !!(form.includeSpouse && form.spouseFirst?.trim() && form.spouseLast?.trim());
    if (spouseIntended && !spouseInRoster) n += 1;
    return n;
  }, [counts.adults, counts.teens, form.category, form.includeSpouse, form.spouseFirst, form.spouseLast]);
  const computedPrice = useMemo(() => {
    // Southern + Hotel Accommodation = $75 per person (adults & teens only; children 0–12 free).
    // Overrides all Individual/Couple/Family/additional-adult pricing.
    if (isHotel) {
      return 75 * Math.max(1, hotelBillablePersons);
    }
    return baseCategoryPrice;
  }, [baseCategoryPrice, isHotel, hotelBillablePersons]);

  const isFreeStudent = false;

  const fullName = `${form.firstName} ${form.lastName}`.trim();
  const categoryLabel = CATEGORY_META[form.category].label;
  const memo =
    form.region && fullName
      ? isSouthern
        ? `${form.region.replace(" Region", "")} Conference + ${fullName} + ${categoryLabel} $${computedPrice}`
        : `${form.region.replace(" Region", "")} Conference + ${fullName}`
      : "";

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const addChild = () => set("children", [...form.children, {
    id: newId(),
    first_name: "",
    last_name: form.lastName,
    age_group: "child_6_12",
    relationship: "child",
    child_category: undefined,
    age: undefined,
  }]);
  const categoryFromAge = (a: number): ChildCategory =>
    a <= 5 ? "child_0_5" : a <= 12 ? "child_6_12" : a <= 17 ? "teen" : "adult_18_plus";
  const updateChild = (id: string, patch: Partial<RosterMember>) =>
    set(
      "children",
      form.children.map((c) => {
        if (c.id !== id) return c;
        const next = { ...c, ...patch } as RosterMember;
        // Age is the source of truth — always derive category & age_group from it.
        if (patch.age !== undefined) {
          if (patch.age === undefined || patch.age === null || Number.isNaN(patch.age as any)) {
            next.child_category = undefined;
          } else {
            const cat = categoryFromAge(patch.age as number);
            next.child_category = cat;
            next.age_group = ageGroupFromChildCategory(cat);
          }
        } else if (patch.child_category) {
          next.age_group = ageGroupFromChildCategory(patch.child_category);
        }
        return next;
      }),
    );
  const removeChild = (id: string) => set("children", form.children.filter((c) => c.id !== id));

  const addAdult = () => {
    setAdultsConfirmed(false);
    setSpouseSuggestDismissed(false);
    set("otherAdults", [...form.otherAdults, {
      id: newId(), first_name: "", last_name: form.lastName, age_group: "adult", relationship: "other_adult", attendee_type: "adult",
    }]);
  };
  const updateAdult = (id: string, patch: Partial<RosterMember>) => {
    setAdultsConfirmed(false);
    set("otherAdults", form.otherAdults.map((a) => a.id === id ? { ...a, ...patch } : a));
  };
  const removeAdult = (id: string) => {
    setAdultsConfirmed(false);
    set("otherAdults", form.otherAdults.filter((a) => a.id !== id));
  };

  const FIELD_LABELS: Record<string, string> = {
    firstName: "First name",
    middleName: "Middle name",
    lastName: "Last name",
    countryCode: "Country code",
    mobile: "Mobile number",
    email: "Email",
    state: "State",
    region: "Region",
    city: "City",
    address: "Mailing address",
    spouseFirst: "Spouse first name",
    spouseLast: "Spouse last name",
    spouseMiddle: "Spouse middle name",
    spouseEmail: "Spouse email",
    spouseMobile: "Spouse mobile number",
    boardingType: "Boarding type",
    children: "Children",
    otherAdults: "Other adults",
    dietary: "Dietary notes",
    category: "Registration category",
    screenshot: "Payment screenshot",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      const items: string[] = [];
      parsed.error.issues.forEach((i) => {
        const key = String(i.path[0] ?? "");
        if (key) errs[key] = i.message;
        const label = FIELD_LABELS[key] || key || "Field";
        items.push(`${label}: ${i.message}`);
      });
      setErrors(errs);
      setErrorModal({ open: true, title: "Please fix the following before submitting", items });
      return;
    }
    // For student/individual, ignore family entries entirely.
    // Couple keeps spouse + any under-3 children entered; family keeps everything.
    const isFamily = form.category === "family";
    const isCouple = form.category === "couple";
    const effSpouseInclude = (isFamily || isCouple) && form.includeSpouse;
    const effChildren = isFamily || isCouple ? form.children : [];
    const effAdults = (isFamily || form.category === "individual") ? form.otherAdults : [];

    // Validate each added member has both names (family only)
    const badChild = effChildren.find((c) => !c.first_name.trim() || !c.last_name.trim());
    const badAdult = effAdults.find((a) => !a.first_name.trim() || !a.last_name.trim());
    if (badChild || badAdult) {
      setErrorModal({
        open: true,
        title: "Family member details incomplete",
        items: ["Every added family member needs a first and last name."],
      });
      return;
    }

    // Southern region: spouse email + mobile are required for Couple / Family
    if (isSouthern && effSpouseInclude) {
      const spouseErrs: Record<string, string> = {};
      const items: string[] = [];
      if (!form.spouseEmail?.trim()) {
        spouseErrs.spouseEmail = "Spouse email is required";
        items.push("Spouse email: required");
      }
      if (!form.spouseMobile?.trim()) {
        spouseErrs.spouseMobile = "Spouse mobile number is required";
        items.push("Spouse mobile number: required");
      }
      if (items.length) {
        setErrors((p) => ({ ...p, ...spouseErrs }));
        setErrorModal({ open: true, title: "Spouse details required", items });
        return;
    }


    // Southern family: block submission if any child row is 18+; user must move them.
    if (isSouthern && isFamily) {
      const adultChild = effChildren.find((c) => (c.age ?? -1) >= 18);
      if (adultChild) {
        setErrorModal({
          open: true,
          title: "Adult listed under Children",
          items: [
            `${(adultChild.first_name || "One child").trim()} is 18 or older. Please use "Move to Additional Adult" above, or edit their age, before submitting.`,
          ],
        });
        return;
      }
      // Confirm the extra-adult fee before running the DB insert. Each Other Adult adds $125
      // (see computeSouthernBasePrice); the audit found this was a common mis-entry.
      if (isSouthern && form.category === "family" && form.otherAdults.length >= 1 && !adultsConfirmed) {
        setAdultConfirmOpen(true);
        return;
      }
    }
    }


    // Duplicate registration precheck
    try {
      const { data: dup } = await (supabase
        .from("conference_registrations" as any) as any)
        .select("id")
        .eq("conference_slug", "regional-conference-2026")
        .ilike("email", form.email.trim())
        .ilike("first_name", form.firstName.trim())
        .ilike("last_name", form.lastName.trim())
        .limit(1)
        .maybeSingle();
      if (dup?.id) {
        setDuplicateModal({ open: true, id: dup.id });
        return;
      }
    } catch (_) {
      // If the precheck fails (network), the DB unique index will still block duplicates.
    }

    // Screenshot is REQUIRED when paying now via Zelle or Online; optional for in_person/pay_later.
    const screenshotRequired = isSouthern && !isFreeStudent && (paymentChoice === "zelle" || paymentChoice === "online");
    if (screenshotRequired && !screenshot) {
      setErrors((p) => ({ ...p, screenshot: "Payment screenshot is required for Pay Now (Zelle / Online)." }));
      setErrorModal({ open: true, title: "Payment screenshot required", items: ["Please attach your Zelle or online payment screenshot. It is mandatory for Pay Now."] });
      return;
    }
    if (screenshot) {
      if (screenshot.size > 8 * 1024 * 1024) {
        setErrors((p) => ({ ...p, screenshot: "File must be 8MB or less" }));
        setErrorModal({ open: true, title: "File too large", items: ["Payment screenshot must be 8MB or less."] });
        return;
      }
      const allowed = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowed.includes(screenshot.type)) {
        setErrors((p) => ({ ...p, screenshot: "Only JPG, PNG, or PDF" }));
        setErrorModal({ open: true, title: "Unsupported file type", items: ["Payment screenshot must be JPG, PNG, or PDF."] });
        return;
      }
    }


    setSubmitting(true);
    try {
      let path: string | null = null;
      if (screenshot && !isFreeStudent) {
        const ext = screenshot.name.split(".").pop() || "bin";
        const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        path = `regional-conference-2026/${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("conference-payments")
          .upload(path, screenshot, { contentType: screenshot.type, upsert: false });
        if (upErr) throw upErr;
      }


      const sp = new URLSearchParams(window.location.search);
      let stored: Record<string, string> = {};
      try { stored = JSON.parse(sessionStorage.getItem("ofuesi_attrib") || "{}"); } catch {}
      const pick = (k: string) => sp.get(k) || stored[k] || null;
      const utmSource = pick("utm_source");
      const utmMedium = pick("utm_medium");
      const utmCampaign = pick("utm_campaign");
      const src = pick("src") || utmSource || (document.referrer ? new URL(document.referrer, window.location.href).hostname : null);
      const referrerUrl = document.referrer || stored.referrer || null;

      const newRegistrationId =
        (typeof crypto !== "undefined" && "randomUUID" in crypto)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const { error: insErr } = await (supabase.from("conference_registrations" as any) as any).insert({
        id: newRegistrationId,
        conference_slug: "regional-conference-2026",
        first_name: form.firstName.trim(),
        middle_name: form.middleName?.trim() || null,
        last_name: form.lastName.trim(),
        mobile_country_code: form.countryCode,
        mobile_number: form.mobile.trim(),
        spouse_mobile_country_code: form.includeSpouse && form.spouseMobile?.trim() ? (form.spouseCountryCode || "+1") : null,
        spouse_mobile_number: form.includeSpouse && form.spouseMobile?.trim() ? form.spouseMobile.trim() : null,
        spouse_first_name: form.includeSpouse ? (form.spouseFirst?.trim() || null) : null,
        spouse_middle_name: form.includeSpouse ? (form.spouseMiddle?.trim() || null) : null,
        spouse_last_name: form.includeSpouse ? (form.spouseLast?.trim() || null) : null,
        spouse_email: form.includeSpouse ? (form.spouseEmail?.trim() || null) : null,
        boarding_type: isSouthern ? (form.boardingType || "camp") : null,
        conference_fee: isSouthern ? computedPrice : null,
        email: form.email.trim(),
        region: form.region,
        state: form.state,
        city: form.city.trim(),
        mailing_address: form.address.trim(),
        children_0_5: counts.c0_5,
        children_6_12: counts.c6_12,
        teenagers_13_17: counts.teens,
        adults_18_plus: counts.adults,
        total_members: counts.total,
        dietary_notes: form.dietary?.trim() || null,
        zelle_memo: memo,
        payment_screenshot_path: path,
        registration_source: src,
        referrer_url: referrerUrl,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        category: form.category,
        category_price: isSouthern ? computedPrice : 0,
        family_type: isSouthern ? familyType : null,
        amount_paid: 0,
        amount_due: isSouthern ? computedPrice : 0,
        payment_choice: isFreeStudent ? "free" : (isSouthern ? paymentChoice : "pay_later"),
        is_student: form.category === "individual" ? !!form.isStudent : false,
        university: form.category === "individual" && form.isStudent ? (form.university?.trim() || null) : null,
        // Treasurer / regional admins confirm payment manually against the bank record.
        // Free students are auto-confirmed; everyone else starts as pending.
        payment_status: isFreeStudent ? "confirmed" : "pending",
        needs_scholarship: !!(
          form.needsScholarship ||
          (form.includeSpouse && form.spouseNeedsScholarship) ||
          form.otherAdults.some((a) => !!a.needs_scholarship)
        ),
        scholarship_requested_by: (() => {
          const parts: string[] = [];
          if (form.needsScholarship) parts.push("Primary");
          if (form.includeSpouse && form.spouseNeedsScholarship) parts.push("Spouse");
          form.otherAdults.forEach((a) => {
            if (a.needs_scholarship) {
              const nm = [a.first_name, a.last_name].filter(Boolean).join(" ").trim();
              parts.push(nm ? `Additional Adult — ${nm}` : "Additional Adult");
            }
          });
          return parts.length ? parts.join("; ") : null;
        })(),
        general_comments: form.generalComments?.trim() || null,
      });

      if (insErr) throw insErr;

      const memberRows = roster.map((m: any) => ({
        registration_id: newRegistrationId,
        first_name: m.first_name,
        middle_name: m.middle_name ?? null,
        last_name: m.last_name,
        relationship: m.relationship,
        age_group: m.age_group,
        age: m.age ?? null,
        child_category: m.child_category ?? null,
        gender: m.gender ?? null,
        special_needs: m.notes ?? null,
        email: m.email ?? null,
        mobile_country_code: m.country_code ?? null,
        mobile_number: m.mobile ?? null,
        attendee_type: m.attendee_type ?? null,
        needs_scholarship: !!m.needs_scholarship,
      }));

      const { error: memErr } = await (supabase.from("registration_members" as any) as any).insert(memberRows);
      if (memErr) console.warn("Member rows insert failed:", memErr);


      supabase.functions
        .invoke("send-conference-registration-confirmation", {
          body: {
            email: form.email.trim(),
            firstName: form.firstName.trim(),
            middleName: form.middleName?.trim() || null,
            lastName: form.lastName.trim(),
            countryCode: form.countryCode,
            mobile: form.mobile.trim(),
            region: form.region,
            state: form.state,
            city: form.city.trim(),
            address: form.address.trim(),
            members: roster.map((m) => ({
              full_name: [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" "),
              relationship: m.relationship,
              age_group: m.age_group,
            })),
            totalMembers: counts.total,
            adults: counts.adults,
            adults18: counts.adults,
            teens: counts.teens,
            teens13_17: counts.teens,
            children6_12: counts.c6_12,
            children0_5: counts.c0_5,
            dietary: form.dietary?.trim() || null,
            zelleMemo: memo,
            zelleRecipient: ZELLE_RECIPIENT,
            category: form.category,
            categoryLabel,
            categoryPrice: isSouthern ? computedPrice : 0,
            paymentChoice: isFreeStudent ? "free" : (isSouthern ? paymentChoice : "pay_later"),
          },
        })
        .catch((err) => console.warn("Confirmation email failed:", err));

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error(err);
      const code = (err && (err.code || err.status)) as string | number | undefined;
      const msg = String(err?.message || "");
      if (code === "23505" || /duplicate key|unique constraint/i.test(msg)) {
        setDuplicateModal({ open: true, id: null });
      } else {
        toast({
          title: "Submission failed",
          description: err?.message || "Please try again.",
          variant: "destructive",
        });
      }

    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const fullNameDisplay = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(" ");
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-amber-50">
        <div className="container mx-auto px-4 py-16 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-sky-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-sky-600 to-amber-500 text-white p-8 text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-3" />
              <h1 className="text-3xl font-bold">Registration Received</h1>
              <p className="opacity-95 mt-1">
                A copy of your registration has been emailed to <strong>{form.email}</strong>.
              </p>
            </div>

            <div className="p-6 md:p-10 space-y-6">
              <ReceiptSection title="Personal Information">
                <ReceiptRow label="Full Name" value={fullNameDisplay} />
                <ReceiptRow label="Email" value={form.email} />
                <ReceiptRow label="Mobile" value={`${form.countryCode} ${form.mobile}`} />
              </ReceiptSection>

              <ReceiptSection title="Region & Address">
                <ReceiptRow label="State" value={form.state} />
                <ReceiptRow label="Region" value={form.region} />
                <ReceiptRow label="City" value={form.city} />
                
              </ReceiptSection>

              {/* Family roster appears in next section */}

                {/* legacy line removed */}
              <ReceiptSection title="Family Members">
                <div className="space-y-2">
                  {roster.map((m, i) => (
                    <div key={i} className="flex items-start justify-between border-b border-slate-100 py-1 text-sm">
                      <div>
                        <span className="font-medium text-slate-900">
                          {[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ")}
                        </span>
                        <span className="ml-2 text-xs text-slate-500 capitalize">({m.relationship.replace("_", " ")})</span>
                      </div>
                      <span className="text-xs text-slate-500">{ageGroupLabel(m.age_group)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs">
                  <SummaryStat label="Total" value={counts.total} highlight />
                  <SummaryStat label="Adults" value={counts.adults} />
                  <SummaryStat label="Teens" value={counts.teens} />
                  <SummaryStat label="Kids 6–12" value={counts.c6_12} />
                  <SummaryStat label="Kids 0–5" value={counts.c0_5} />
                </div>
                <ReceiptRow label="Dietary / Allergy Notes" value={form.dietary || "—"} />
              </ReceiptSection>

              {isSouthern ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-slate-700 space-y-2">
                <p className="font-semibold">Payment — status: <span className="text-amber-700">pending treasurer confirmation</span></p>
                {paymentChoice === "zelle" && (
                  <>
                    <p>Send <strong>${computedPrice}</strong> via Zelle to <strong>{ZELLE_RECIPIENT}</strong> (OFUESI Treasurer).</p>
                    <p>Memo: <span className="font-mono bg-white px-2 py-1 rounded">{memo}</span></p>
                  </>
                )}
                {paymentChoice === "online" && (
                  <>
                    <p>Pay <strong>${computedPrice}</strong> via the <a href="/give" className="text-sky-700 underline" target="_blank" rel="noopener">Give page</a> (PayPal / Card).</p>
                    <p>Add this note: <span className="font-mono bg-white px-2 py-1 rounded">{memo}</span></p>
                  </>
                )}
                {paymentChoice === "in_person" && (
                  <p>Bring a cheque payable to <strong>OFUESI</strong> (memo: <span className="font-mono bg-white px-1.5 py-0.5 rounded">{memo}</span>) or pay in person at the welcome desk.</p>
                )}
                {paymentChoice === "pay_later" && (
                  <p>You chose <strong>Pay Later</strong>. Return to the <strong>Payment Confirmation</strong> tab once you've paid to upload your receipt.</p>
                )}
                <p className="text-xs text-slate-500">
                  Your regional treasurer will mark your registration as paid once the bank record matches.
                </p>
              </div>
              ) : (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-slate-700 space-y-2">
                <p className="font-semibold">Registration received</p>
                <p>Pricing and payment details for your region will be shared by your regional coordinator. No payment is required at this time.</p>
              </div>
              )}

              <div className="text-center pt-2">
                <Button onClick={() => window.print()} variant="outline" className="mr-2">
                  Print Receipt
                </Button>
                <Button asChild className="bg-gradient-to-r from-sky-600 to-amber-500 text-white">
                  <a href="/">Return Home</a>
                </Button>
              </div>

              <p className="italic text-slate-600 text-center pt-2">
                "May the Lord direct your hearts into God's love and Christ's perseverance." — 2 Thessalonians 3:5
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? "scroll-smooth" : "min-h-screen bg-gradient-to-b from-sky-50 via-white to-amber-50 scroll-smooth"}
      style={
        !embedded && selectedConference?.flyer
          ? {
              backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,248,235,0.96)), url(${selectedConference.flyer})`,
              backgroundAttachment: "fixed",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      {/* Theme song announcement ribbon */}

      {/* Hero with logo (hidden when embedded inside Conferences page) */}
      {!embedded && (
        selectedConference?.heroImage ? (
          <section className="relative overflow-hidden bg-[hsl(220,70%,14%)]">
            <img
              src={selectedConference.heroImage}
              alt={`${selectedConference.displayRegionName} conference backdrop`}
              className="w-full h-auto block"
            />
          </section>
        ) : (
          <section className="relative overflow-hidden text-white py-10 md:py-16 px-4 bg-gradient-to-br from-[hsl(220,70%,14%)] via-[hsl(215,65%,22%)] to-[hsl(28,85%,38%)]">
            {/* Decorative radial glow — works on every viewport */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-60"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, rgba(252,211,77,0.25), transparent 55%), radial-gradient(circle at 80% 80%, rgba(56,189,248,0.25), transparent 55%)",
              }}
            />
            {/* Flyer overlay — desktop only, subtle texture */}
            {selectedConference?.flyer && (
              <div
                aria-hidden
                className="hidden md:block absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
                style={{
                  backgroundImage: `url(${selectedConference.flyer})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
            <div className="container mx-auto max-w-4xl text-center relative z-10">
              <motion.img
                src={ofuesiLogo}
                alt="OFUESI logo"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 md:mb-6 rounded-full bg-white/95 p-2 shadow-2xl ring-4 ring-amber-300/60"
              />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4 drop-shadow leading-tight"
              >
                OFUESI Annual Regional Conference 2026
              </motion.h1>
              {selectedConference && (
                <p className="text-sm sm:text-base md:text-lg text-white/95 mb-3 font-medium">
                  {selectedConference.displayRegionName} · {formatConferenceDateRange(selectedConference.start, selectedConference.end, "long")}{selectedConference.timeLabel && <span> · {selectedConference.timeLabel}</span>}{selectedConference.slug === "southwest" && <span className="ml-2 text-amber-200 text-xs sm:text-sm">(Tentative)</span>}
                </p>
              )}
              {/* Theme verse — Ezekiel 37:5 */}
              <div className="mx-auto max-w-2xl mt-4 mb-2 px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg">
                <p className="italic text-white text-sm sm:text-base md:text-lg leading-relaxed">
                  This is what the Sovereign Lord says: Look! I am going to put breath into you and make you live again!
                </p>
                <p className="mt-1 text-amber-200 text-xs sm:text-sm font-semibold tracking-wide">
                  — Ezekiel 37:5
                </p>
              </div>
              <p className="italic text-white/90 max-w-2xl mx-auto text-xs sm:text-sm md:text-base hidden sm:block mt-3">
                "Please plan ahead for your travel and pray that God will speak to each of us and guide
                OFUESI toward His mission."
              </p>
            </div>
          </section>
        )
      )}

      {/* No floating QR panel here — each region's flyer/banner already
          contains its own QR inside the existing artwork placeholder. */}

      {/* Hero → Register tab pointer */}
      {!embedded && selectedRegionId && tab !== "register" && (
        <div className="flex justify-center -mt-3 mb-3 px-4">
          <button
            type="button"
            onClick={() => {
              setTab("register");
              setTimeout(() => {
                document.getElementById("conf-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
            className="group inline-flex flex-col items-center focus:outline-none"
            aria-label="Jump to Register tab"
          >
            <span className="text-sm sm:text-base font-bold tracking-wide bg-gradient-to-r from-amber-400 to-amber-500 text-white border-2 border-amber-300 rounded-full px-4 py-1.5 shadow-lg ring-2 ring-amber-200 animate-pulse">
              Register here ↓
            </span>
            <motion.span
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
              className="mt-1 inline-flex"
              aria-hidden
            >
              <ArrowRight
                className="w-12 h-12 sm:w-14 sm:h-14 rotate-90 text-amber-500"
                strokeWidth={3}
                style={{ filter: "drop-shadow(0 2px 6px rgba(245,158,11,0.7))" }}
              />
            </motion.span>
          </button>
        </div>
      )}

      {/* Live stream banner (regions currently streaming) */}
      {!embedded && (
        <LiveStreamCallout slug={routeSlug || selectedConference?.slug} className="mb-4" />
      )}


      {/* Tabs */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 lg:py-10 max-w-5xl">

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex flex-wrap justify-center gap-1.5 sm:gap-2 h-auto bg-white/70 backdrop-blur p-1.5 sm:p-2 rounded-xl border border-sky-100 shadow-sm mb-3 sm:mb-6 w-full scroll-mt-16" id="conf-tabs">
            {[
              { value: "details", icon: <Info className="w-4 h-4 mr-1.5 shrink-0" />, label: "Conference Details" },
              { value: "register", icon: <ClipboardList className="w-4 h-4 mr-1.5 shrink-0" />, label: "Register" },
              ...(isSouthern ? [{ value: "schedule", icon: <CalendarClock className="w-4 h-4 mr-1.5 shrink-0" />, label: "Program Schedule" }] : []),
              { value: "worship", icon: <Music className="w-4 h-4 mr-1.5 shrink-0" />, label: "Worship" },
              ...(isSouthern ? [{ value: "volunteer", icon: <HandHeart className="w-4 h-4 mr-1.5 shrink-0" />, label: "Volunteer Sign-Up" }] : []),
              ...(isSouthern ? [{ value: "payment", icon: <Upload className="w-4 h-4 mr-1.5 shrink-0" />, label: "Payment Confirmation" }] : []),
              { value: "cancel", icon: <XCircle className="w-4 h-4 mr-1.5 shrink-0" />, label: "Cancel Request" },

              { value: "feedback", icon: <MessageSquareHeart className="w-4 h-4 mr-1.5 shrink-0" />, label: "Feedback" },
            ].map((t) => {
              const isRegister = t.value === "register";
              const highlight = isRegister && tab !== "register";
              return (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className={`relative flex-1 min-w-[44%] sm:min-w-[140px] md:flex-initial inline-flex items-center justify-center text-center whitespace-normal break-words leading-tight px-3 py-2 text-xs sm:text-sm h-auto data-[state=active]:bg-gradient-to-r data-[state=active]:from-sky-600 data-[state=active]:to-amber-500 data-[state=active]:text-white data-[state=active]:shadow-md ${
                    highlight
                      ? "bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-lg ring-2 ring-amber-300 animate-pulse"
                      : ""
                  }`}
                >
                  {highlight && (
                    <span className="pointer-events-none absolute -inset-0.5 rounded-md bg-amber-400/40 animate-ping" aria-hidden />
                  )}
                  <span className="relative inline-flex items-center">
                    {t.icon}<span>{t.label}</span>
                    {highlight && (
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                        className="ml-1.5 inline-flex"
                        aria-hidden
                      >
                        <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    )}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>






          {/* CONFERENCE DETAILS — flyer, venue, program, worship */}
          <TabsContent value="details">
            {(!selectedRegionId || !selectedRegion) ? (
              dataLoading ? (
                <InfoCard icon={<Loader2 className="w-6 h-6 text-sky-600 animate-spin" />} title="Loading conference details…">
                  <p className="text-slate-600">
                    Fetching the latest flyer, venue and program for your region. This usually takes a few seconds.
                  </p>
                </InfoCard>
              ) : (
                <InfoCard icon={<Info className="w-6 h-6 text-amber-600" />} title="Select a region first">
                  <p className="text-slate-600 mb-4">
                    Please choose your region from the Conferences page to see the flyer, venue,
                    program and worship details.
                  </p>
                  <Button asChild className="bg-gradient-to-r from-sky-600 to-amber-500 text-white">
                    <a href="/conference">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Go to Conferences
                    </a>
                  </Button>
                </InfoCard>
              )
            ) : (
              <RegionDetailsPanel
                region={selectedRegion}
                flyer={flyerByRegion(selectedRegionId)}
                venues={venuesByRegion(selectedRegionId)}
                program={programByRegion(selectedRegionId)}
                worship={worshipByRegion(selectedRegionId)}
                conference={selectedConference}
                onBack={() => setSelectedRegionId(null)}
                onRegister={() => {
                  if (selectedRegion) set("region", selectedRegion.name);
                  setTab("register");
                }}
              />
            )}
          </TabsContent>

          {/* WORSHIP — theme song, songs list, suggest a song */}
          <TabsContent value="worship">
            <Suspense fallback={<div className="rounded-xl border border-sky-100 bg-white p-6 text-center text-sm text-slate-500">Loading worship…</div>}>
              <WorshipTabPanel
                regionName={selectedRegion?.name || ""}
                worshipCommon={worshipRowsCommon}
                worshipRegion={selectedRegionId ? worshipByRegion(selectedRegionId) : []}
              />
            </Suspense>
          </TabsContent>

          {/* VOLUNTEER SIGN-UP — Southern Region external form */}
          <TabsContent value="volunteer">
            <div className="rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-amber-50 p-6 sm:p-10 shadow-sm">
              <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-600 to-amber-500 text-white flex items-center justify-center mb-4 shadow-md">
                  <HandHeart className="w-8 h-8" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  Volunteer Sign-Up
                </h2>
                <p className="text-sm sm:text-base text-slate-600 mb-1">
                  OFUESI Southern Region Conference 2026
                </p>
                <p className="text-sm text-slate-600 mb-6">
                  Serving together makes the conference possible. Sign up to volunteer for hospitality,
                  registration, worship, children's ministry, ushering, media, food service, and more.
                </p>
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-sky-600 to-amber-500 hover:from-sky-700 hover:to-amber-600 text-white shadow-md"
                >
                  <a
                    href="https://id-preview--2d4f4fbe-b353-4f65-956b-f19aee2d4197.lovable.app/southern-volunteer-signup"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <HandHeart className="w-5 h-5 mr-2" />
                    Sign Up to Volunteer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <p className="mt-4 text-xs text-slate-500">
                  Opens the volunteer signup form in a new tab.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="register">
            {!selectedRegionId && (
              dataLoading ? (
                <InfoCard icon={<Loader2 className="w-6 h-6 text-sky-600 animate-spin" />} title="Loading registration…">
                  <p className="text-slate-600">Preparing your region's registration form.</p>
                </InfoCard>
              ) : (
                <InfoCard icon={<ClipboardList className="w-6 h-6 text-amber-600" />} title="Select a region first">
                  <p className="text-slate-600 mb-4">
                    Please choose your region from the Conferences page to continue to the registration form.
                  </p>
                  <Button asChild className="bg-gradient-to-r from-sky-600 to-amber-500 text-white">
                    <a href="/conference">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Go to Conferences
                    </a>
                  </Button>
                </InfoCard>
              )
            )}


            {selectedRegionId && (
              <>
                {isSouthern && (
                  <div className="mb-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setTab("schedule")}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#0b1e3f] bg-amber-100 hover:bg-amber-200 border border-amber-300 transition"
                    >
                      <CalendarClock className="w-3.5 h-3.5" /> View Program Schedule
                    </button>
                  </div>
                )}
                <div className={isSouthern ? "lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6 lg:items-start" : ""}>
                  <form id="conference-registration-form" onSubmit={handleSubmit} className="space-y-8 min-w-0">


              <Section
                title="1. Choose your Registration Category"
                subtitle={isSouthern
                  ? "Pick the option that best matches you below — pricing updates automatically."
                  : "Pick the option that best matches you below. Pricing for this region will be announced soon."}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(["individual","couple","family"] as const).map((k) => {
                    const meta = CATEGORY_META[k];
                    const active = form.category === k;
                    return (
                      <button
                        type="button"
                        key={k}
                        onClick={() => setForm((p) => ({
                          ...p,
                          category: k,
                          includeSpouse: k === "couple" || k === "family" ? p.includeSpouse : false,
                          // Couple must not carry any children — clear them when switching to Couple.
                          children: k === "family" ? p.children : [],
                          otherAdults: k === "family" ? p.otherAdults : [],
                        }))}
                        className={`group relative text-left rounded-2xl border-2 p-5 transition-all overflow-hidden ${
                          active
                            ? `${meta.selectedBorder} ${meta.selectedBg} ${meta.selectedRing} shadow-lg`
                            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                        }`}
                      >
                        {active && (
                          <span className={`absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 border ${meta.badge}`}>
                            <CheckCircle2 className="w-3 h-3" /> Selected
                          </span>
                        )}
                        <div className="mb-1.5 pr-16">
                          <span className={`font-semibold text-base ${active ? meta.accent : "text-slate-800"}`}>{meta.label}</span>
                        </div>
                        {isSouthern && (
                          <div className="mb-2">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Estimated Starting Price</div>
                            <div className={`text-lg font-bold ${active ? meta.accent : "text-slate-900"}`}>
                              ${SOUTHERN_STARTING_PRICE[k]}
                              <span className="ml-1 text-[10px] font-medium text-slate-500 uppercase tracking-wide">est.</span>
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {meta.description}
                        </p>
                        {isSouthern && k === "family" && (
                          <p className="mt-1.5 text-[10.5px] italic text-slate-500 leading-snug">
                            Estimated starting price — not the final amount.
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
                {isSouthern && form.category === "family" && (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-slate-700">
                    <p className="mt-1">Add each family member below. Your Registration Fee Summary will be calculated after you've entered all attendee details.</p>
                  </div>
                )}

                {form.category === "individual" && (
                  <div className="mt-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-5 text-sm md:text-base text-slate-800 space-y-4 shadow-sm">
                    {isSouthern && (
                      <p className="leading-relaxed">
                        <span className="inline-block bg-emerald-600 text-white font-bold uppercase tracking-wide text-xs px-2 py-0.5 rounded mr-2 align-middle">Important</span>
                        <span className="font-semibold text-emerald-900">Scholarships are available for students</span> — for all scholarship-related queries, please contact{" "}
                        <span className="font-semibold text-emerald-900">Suresh Konatham</span> at{" "}
                        <a href="tel:+15124831132" className="font-semibold text-emerald-800 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900">+1 512-483-1132</a>{" "}
                        before completing payment.
                      </p>
                    )}
                    <label className="flex items-start gap-3 cursor-pointer rounded-lg bg-white border-2 border-emerald-300 px-4 py-3 hover:border-emerald-500 hover:bg-emerald-50 transition">
                      <input
                        type="checkbox"
                        className="mt-1 w-5 h-5 accent-emerald-600"
                        checked={!!form.isStudent}
                        onChange={(e) => set("isStudent", e.target.checked)}
                      />
                      <span className="font-semibold text-emerald-900 text-base">I am currently a student</span>
                    </label>
                    {form.isStudent && (
                      <div>
                        <Label htmlFor="ind-university" className="text-sm font-medium">University / College <span className="text-rose-600">*</span></Label>
                        <Input
                          id="ind-university"
                          value={form.university || ""}
                          onChange={(e) => set("university", e.target.value)}
                          placeholder="e.g. University of Houston"
                          maxLength={200}
                        />
                        {errors.university && <p className="text-xs text-rose-600 mt-1">{errors.university}</p>}
                      </div>
                    )}
                    {isSouthern && (
                    <label className="flex items-start gap-3 cursor-pointer rounded-lg bg-white border-2 border-amber-300 px-4 py-3 hover:border-amber-500 hover:bg-amber-50 transition">
                      <input
                        type="checkbox"
                        className="mt-1 w-5 h-5 accent-amber-600"
                        checked={!!form.needsScholarship}
                        onChange={(e) => set("needsScholarship", e.target.checked)}
                      />
                      <span className="text-sm text-slate-800">
                        <span className="font-semibold text-amber-900">Need scholarship assistance</span>
                        <span className="block text-xs text-slate-600 mt-0.5">This does not change your fee — admins will review and follow up.</span>
                      </span>
                    </label>
                    )}
                  </div>
                )}
              </Section>




              <Section title={`2. Personal Information`} subtitle="All fields required unless noted">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="First Name" error={errors.firstName}>

                    <Input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
                  </Field>
                  <Field label="Middle Name (optional)">
                    <Input value={form.middleName} onChange={(e) => set("middleName", e.target.value)} />
                  </Field>
                  <Field label="Last Name" error={errors.lastName}>
                    <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Field label="Mobile Number" error={errors.mobile}>
                    <div className="flex gap-2">
                      <Select value={form.countryCode} onValueChange={(v) => set("countryCode", v)}>
                        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="flex-1"
                        name="tel"
                        type="tel"
                        autoComplete="tel-national"
                        value={form.mobile}
                        onChange={(e) => set("mobile", e.target.value)}
                        onBlur={(e) => { if (e.target.value !== form.mobile) set("mobile", e.target.value); }}
                        onInput={(e) => { const v = (e.target as HTMLInputElement).value; if (v !== form.mobile) set("mobile", v); }}
                        placeholder="555 123 4567"
                        inputMode="tel"
                      />
                    </div>
                  </Field>
                  <Field label="Email Address" error={errors.email}>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@example.com"
                    />
                  </Field>
                </div>
              </Section>


              {form.category === "individual" && (
                <Section
                  title="3. Additional Attendees (Optional)"
                  subtitle="Register one or more adults or students under a single registration and payment. This option is ideal for friends, siblings, church members, youth groups, or anyone registering together."
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-slate-600">
                      {form.otherAdults.length === 0
                        ? "No additional attendees yet. Click below to add one."
                        : `${form.otherAdults.length} additional ${form.otherAdults.length === 1 ? "attendee" : "attendees"} added.`}
                    </div>
                    <Button type="button" size="sm" onClick={addAdult} className="bg-sky-600 hover:bg-sky-700 text-white">
                      <Plus className="w-4 h-4 mr-1" /> Add Another Attendee
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {form.otherAdults.map((a, idx) => {
                      const type = a.attendee_type === "student" ? "student" : "adult";
                      const fee = attendeeFee(type);
                      const typeLabel = type === "student" ? "Student" : "Adult";
                      return (
                      <div key={a.id} className="rounded-xl border border-sky-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-slate-800">
                            Attendee #{idx + 2} <span className="ml-2 text-xs font-medium text-sky-700">({typeLabel})</span>
                          </div>
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeAdult(a.id)} aria-label="Remove attendee">
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Attendee type <span className="text-rose-600">*</span></Label>
                            <Select value={type} onValueChange={(v) => updateAdult(a.id, { attendee_type: v as "adult" | "student" })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="adult">Adult</SelectItem>
                                <SelectItem value="student">Student</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="hidden md:block" />
                          <div>
                            <Label className="text-xs">First name <span className="text-rose-600">*</span></Label>
                            <Input value={a.first_name} onChange={(e) => updateAdult(a.id, { first_name: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">Last name <span className="text-rose-600">*</span></Label>
                            <Input value={a.last_name} onChange={(e) => updateAdult(a.id, { last_name: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">Mobile number <span className="text-rose-600">*</span></Label>
                            <div className="flex gap-2">
                              <Select value={a.country_code || "+1"} onValueChange={(v) => updateAdult(a.id, { country_code: v })}>
                                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {COUNTRY_CODES.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                className="flex-1"
                                type="tel"
                                value={a.mobile || ""}
                                onChange={(e) => updateAdult(a.id, { mobile: e.target.value })}
                                placeholder="555 123 4567"
                                inputMode="tel"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Email address <span className="text-rose-600">*</span></Label>
                            <Input type="email" value={a.email || ""} onChange={(e) => updateAdult(a.id, { email: e.target.value })} placeholder="them@example.com" />
                          </div>
                          <div>
                            <Label className="text-xs">Gender</Label>
                            <Select value={a.gender || ""} onValueChange={(v) => updateAdult(a.id, { gender: v })}>
                              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-slate-500">
                          Region: <span className="font-medium text-slate-700">{form.region || "same as primary registrant"}</span>
                          {isSouthern && (<> · Fee: <span className="font-semibold text-emerald-700">${fee}</span></>)}
                        </div>
                        {isSouthern && (
                        <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-amber-600"
                            checked={!!a.needs_scholarship}
                            onChange={(e) => updateAdult(a.id, { needs_scholarship: e.target.checked })}
                          />
                          <span><span className="font-semibold text-amber-900">Needs scholarship assistance</span></span>
                        </label>
                        )}

                      </div>

                    );})}
                  </div>
                </Section>
              )}

              {(form.category === "family" || form.category === "couple") && (

              <Section
                title={form.category === "couple" ? "3. Spouse Details" : "3. Family Members"}

                subtitle={form.category === "couple"
                  ? "Tell us about your spouse so we can welcome them by name."
                  : "Tell us each family member traveling with you so we can welcome them by name."}
              >

                {/* Spouse */}
                <div className="rounded-lg border border-sky-100 bg-sky-50/40 p-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={form.includeSpouse}
                      onCheckedChange={(v) => set("includeSpouse", !!v)}
                    />
                    I'm registering with my spouse
                  </label>
                  {form.includeSpouse && (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                      <Field label="Spouse First Name" error={errors.spouseFirst}>
                        <Input value={form.spouseFirst || ""} onChange={(e) => set("spouseFirst", e.target.value)} />
                      </Field>
                      <Field label="Middle Name (optional)">
                        <Input value={form.spouseMiddle || ""} onChange={(e) => set("spouseMiddle", e.target.value)} />
                      </Field>
                      <Field label="Spouse Last Name" error={errors.spouseLast}>
                        <Input value={form.spouseLast || ""} onChange={(e) => set("spouseLast", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <Field label={`Spouse Mobile Number${isSouthern ? "" : " (optional)"}`} error={errors.spouseMobile}>
                        <div className="flex gap-2">
                          <Select value={form.spouseCountryCode || "+1"} onValueChange={(v) => set("spouseCountryCode", v)}>
                            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {COUNTRY_CODES.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            className="flex-1"
                            name="spouse-tel"
                            type="tel"
                            autoComplete="off"
                            value={form.spouseMobile || ""}
                            onChange={(e) => set("spouseMobile", e.target.value)}
                            onBlur={(e) => { if (e.target.value !== (form.spouseMobile || "")) set("spouseMobile", e.target.value); }}
                            placeholder="555 123 4567"
                            inputMode="tel"
                          />
                        </div>
                      </Field>
                      <Field label={`Spouse Email Address${isSouthern ? "" : " (optional)"}`} error={errors.spouseEmail}>
                        <Input
                          type="email"
                          value={form.spouseEmail || ""}
                          onChange={(e) => set("spouseEmail", e.target.value)}
                          placeholder="spouse@example.com"
                        />
                      </Field>
                    </div>

                    {isSouthern && (
                    <label className="mt-3 flex items-start gap-3 cursor-pointer rounded-lg bg-white border-2 border-amber-300 px-4 py-3 hover:border-amber-500 hover:bg-amber-50 transition">
                      <input
                        type="checkbox"
                        className="mt-1 w-5 h-5 accent-amber-600"
                        checked={!!form.spouseNeedsScholarship}
                        onChange={(e) => set("spouseNeedsScholarship", e.target.checked)}
                      />
                      <span className="text-sm text-slate-800">
                        <span className="font-semibold text-amber-900">Spouse needs scholarship assistance</span>
                        <span className="block text-xs text-slate-600 mt-0.5">Does not change your fee — captured for admin review.</span>
                      </span>
                    </label>
                    )}
                    </>
                  )}
                </div>

                {/* Primary registrant scholarship (couple/family) */}
                {isSouthern && (
                <label className="mt-3 flex items-start gap-3 cursor-pointer rounded-lg bg-white border-2 border-amber-300 px-4 py-3 hover:border-amber-500 hover:bg-amber-50 transition">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5 accent-amber-600"
                    checked={!!form.needsScholarship}
                    onChange={(e) => set("needsScholarship", e.target.checked)}
                  />
                  <span className="text-sm text-slate-800">
                    <span className="font-semibold text-amber-900">Need scholarship assistance (Primary registrant)</span>
                    <span className="block text-xs text-slate-600 mt-0.5">Does not change your fee — captured for admin review.</span>
                  </span>
                </label>
                )}



                {form.category === "family" && (<>
                {/* Children */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">Children</h4>
                      <p className="text-[11px] text-emerald-700 font-medium">Add all children traveling with you.</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={addChild}>
                      <Plus className="w-4 h-4 mr-1" /> Add Child
                    </Button>
                  </div>
                  {form.children.length === 0 && (
                    <p className="text-xs text-slate-500 italic">No children added yet.</p>
                  )}
                  <div className="space-y-2">
                    {form.children.map((c, idx) => {
                      const childErrorKey = (field: string) => errors[`children.${idx}.${field}`] || (idx === 0 ? errors.children : undefined);
                      const derivedCategoryLabel = c.age === undefined
                        ? "Enter age to auto-assign"
                        : c.child_category === "child_0_5" ? "Child 0–5 (Free)"
                        : c.child_category === "child_6_12" ? "Child 6–12"
                        : c.child_category === "teen" ? "Teen 13–17"
                        : c.child_category === "adult_18_plus" ? "Adult 18+"
                        : "—";
                      return (
                        <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-lg p-3 bg-white">
                          <div className="md:col-span-3">
                            <Label className="text-xs">Child Name</Label>
                            <Input value={c.first_name} onChange={(e) => updateChild(c.id, { first_name: e.target.value })} placeholder="First name" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Last name</Label>
                            <Input value={c.last_name} onChange={(e) => updateChild(c.id, { last_name: e.target.value })} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Actual Age</Label>
                            <Input
                              type="number"
                              min={0}
                              max={120}
                              step={1}
                              inputMode="numeric"
                              value={c.age ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "") {
                                  updateChild(c.id, { age: undefined });
                                  return;
                                }
                                const n = Math.floor(Number(v));
                                if (Number.isNaN(n)) return;
                                updateChild(c.id, { age: Math.max(0, Math.min(120, n)) });
                              }}
                              placeholder="Age"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Category (auto)</Label>
                            <div className="h-10 px-3 rounded-md border border-input bg-slate-50 text-sm flex items-center text-slate-700">
                              {derivedCategoryLabel}
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Gender (optional)</Label>
                            <Select value={c.gender || ""} onValueChange={(v) => updateChild(c.id, { gender: v })}>
                              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-1 flex md:justify-end">
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeChild(c.id)} aria-label="Remove child">
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          <div className="md:col-span-12">
                            <Label className="text-xs">Special needs / Allergies (optional)</Label>
                            <Input value={c.notes || ""} onChange={(e) => updateChild(c.id, { notes: e.target.value })} placeholder="e.g. peanut allergy, mobility aid" />
                          </div>
                          {childErrorKey("first_name") && <div className="md:col-span-12 text-xs text-rose-600">{childErrorKey("first_name")}</div>}
                          {childErrorKey("age") && <div className="md:col-span-12 text-xs text-rose-600">{childErrorKey("age")}</div>}
                          {isSouthern && (c.age ?? -1) >= 18 && (
                            <div className="md:col-span-12 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center justify-between gap-2">
                              <span>
                                <span className="font-semibold">{(c.first_name || "This attendee").trim()}</span> is 18 or older and will be treated as an Adult (adds <span className="font-semibold">$125</span>). Move to Additional Adults?
                              </span>
                              <span className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => {
                                    setForm((p) => ({
                                      ...p,
                                      children: p.children.filter((x) => x.id !== c.id),
                                      otherAdults: [
                                        ...p.otherAdults,
                                        {
                                          id: newId(),
                                          first_name: c.first_name,
                                          middle_name: c.middle_name,
                                          last_name: c.last_name || p.lastName,
                                          age_group: "adult",
                                          relationship: "other_adult",
                                          attendee_type: "adult",
                                          gender: c.gender,
                                        },
                                      ],
                                    }));
                                    toast({ title: "Moved to Additional Adults" });
                                  }}
                                >
                                  Move to Additional Adult
                                </Button>
                                <Button type="button" size="sm" variant="outline" onClick={() => updateChild(c.id, { age: undefined })}>
                                  Edit age
                                </Button>
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Other adults */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-slate-700">Other adult family members (parents, in-laws, siblings)</h4>
                    <Button type="button" size="sm" variant="outline" onClick={addAdult}>
                      <Plus className="w-4 h-4 mr-1" /> Add Adult
                    </Button>
                  </div>
                  {form.otherAdults.length === 0 && (
                    <p className="text-xs text-slate-500 italic">No other adults added.</p>
                  )}
                  {isSouthern && !form.includeSpouse && form.otherAdults.length === 1 && !spouseSuggestDismissed && (() => {
                    const a = form.otherAdults[0];
                    const nm = [a.first_name, a.last_name].filter(Boolean).join(" ").trim() || "this attendee";
                    return (
                      <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                        <div className="font-semibold mb-1">Did you mean to register {nm} as your spouse?</div>
                        <div className="mb-2">Each Additional Adult beyond husband and wife adds $125 to your registration fee.</div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => {
                              setForm((p) => ({
                                ...p,
                                includeSpouse: true,
                                spouseFirst: a.first_name || p.spouseFirst,
                                spouseMiddle: a.middle_name || p.spouseMiddle,
                                spouseLast: a.last_name || p.spouseLast,
                                spouseEmail: a.email || p.spouseEmail,
                                spouseCountryCode: a.country_code || p.spouseCountryCode,
                                spouseMobile: a.mobile || p.spouseMobile,
                                otherAdults: p.otherAdults.filter((x) => x.id !== a.id),
                              }));
                              setSpouseSuggestDismissed(true);
                              toast({ title: "Moved to Spouse", description: `${nm} is now the spouse on this registration.` });
                            }}
                          >
                            Move to Spouse
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setSpouseSuggestDismissed(true)}>
                            Keep as Additional Adult
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="space-y-2">
                    {form.otherAdults.map((a) => {
                      const sameSurname = isSouthern
                        && !!a.last_name?.trim()
                        && !!form.lastName?.trim()
                        && a.last_name.trim().toLowerCase() === form.lastName.trim().toLowerCase();
                      const nm = [a.first_name, a.last_name].filter(Boolean).join(" ").trim() || "This attendee";
                      return (
                      <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-lg p-3 bg-white">
                        <div className="md:col-span-5">
                          <Label className="text-xs">First name</Label>
                          <Input value={a.first_name} onChange={(e) => updateAdult(a.id, { first_name: e.target.value })} />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-xs">Last name</Label>
                          <Input value={a.last_name} onChange={(e) => updateAdult(a.id, { last_name: e.target.value })} />
                        </div>
                        <div className="md:col-span-1 flex md:justify-end">
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeAdult(a.id)} aria-label="Remove adult">
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        {sameSurname && (
                          <div className="md:col-span-12 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                            <span className="font-semibold">{nm}</span> shares your last name. Additional Adults add <span className="font-semibold">$125</span> each — please confirm they are not your spouse or a child under 18.
                          </div>
                        )}
                        {isSouthern && (
                        <div className="md:col-span-12">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-amber-600"
                              checked={!!a.needs_scholarship}
                              onChange={(e) => updateAdult(a.id, { needs_scholarship: e.target.checked })}
                            />
                            <span className="font-semibold text-amber-900">Needs scholarship assistance</span>
                          </label>
                        </div>
                        )}
                      </div>
                    );})}
                  </div>
                </div>

                </>)}

                {/* Live summary */}
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 inline-flex items-center gap-2">
                      <Users className="w-4 h-4" /> Total Members
                    </span>
                    <span className="text-2xl font-bold text-slate-700">{counts.total}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <SummaryStat label="Adults 18+" value={counts.adults} />
                    <SummaryStat label="Teens 13–17" value={counts.teens} />
                    <SummaryStat label="Kids 6–12" value={counts.c6_12} />
                    <SummaryStat label="Kids 0–5" value={counts.c0_5} />
                  </div>
                  {roster.length > 0 && (
                    <div className="text-xs text-slate-700 bg-white rounded p-2 border border-sky-100">
                      <div className="font-medium mb-1">Names you've added:</div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {roster.map((m, i) => (
                          <li key={i}>
                            {[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ") || <em className="text-slate-400">(name pending)</em>}
                            <span className="text-slate-400"> — {m.relationship.replace("_", " ")}, {ageGroupLabel(m.age_group)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <Field label="Dietary Restrictions / Food Allergies / Medical Allergies (optional)">
                    <Textarea
                      rows={3}
                      value={form.dietary}
                      onChange={(e) => set("dietary", e.target.value)}
                      placeholder="e.g., vegetarian, peanut allergy, dairy-free…"
                    />
                  </Field>
                </div>
              </Section>
              )}


              <Section title={`4. Region & Address`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="State" error={errors.state}>
                    <Select
                      value={form.state}
                      onValueChange={(v) => {
                        const matched = regions.find((r) =>
                          Array.isArray(r.states) && r.states.includes(v)
                        );
                        setForm((p) => ({
                          ...p,
                          state: v,
                          region: matched?.name || "",
                        }));
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select your state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {selectedRegion?.states && selectedRegion.states.length > 0
                        ? selectedRegion.states.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))
                        : US_STATES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                    </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Region (auto)" error={errors.region}>
                    <Input
                      value={form.region}
                      readOnly
                      placeholder={form.state ? "No region mapped — pick another state" : "Select a state first"}
                      className="bg-slate-50"
                    />
                  </Field>
                  <Field label="City" error={errors.city}>
                    <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Mailing Address (street, apt, ZIP)" error={errors.address}>
                    <Textarea
                      rows={2}
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      placeholder="123 Main St, Apt 4B, City, State, ZIP"
                    />
                  </Field>
                </div>
              </Section>

              {/* Optional Hotel Accommodation override — Southern only */}
              {isSouthern && (
                <Section
                  title="5. Staying Off Campus?"
                  subtitle="Camp accommodation is included with your registration by default. Only use this option if you'll be arranging your own off-campus accommodation."
                >
                  <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4 mb-3">
                    <p className="text-sm font-semibold text-slate-800 mb-1">For those staying off campus:</p>
                    <p className="text-sm text-slate-700">
                      Conference Registration &amp; Food (6 Meals): <strong>$75 per person</strong> (adults &amp; teens; children 0–12 free)
                    </p>
                  </div>
                  <label className="flex items-start gap-3 rounded-xl border-2 border-sky-200 bg-white p-4 cursor-pointer hover:border-sky-400 transition-colors">
                    <Checkbox
                      checked={form.boardingType === "hotel"}
                      onCheckedChange={(v) => set("boardingType", v ? "hotel" : "camp")}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 text-sm">I will arrange my own accommodation</div>
                      <div className="text-xs text-slate-600 mt-0.5">Selecting this changes your registration fee to <strong>$75 per person</strong> for adults &amp; teens (children 0–12 free); registration &amp; 6 meals only.</div>
                    </div>
                  </label>

                  {form.boardingType === "hotel" && (
                    <div className="mt-4 rounded-xl border border-sky-200 bg-white p-5 md:p-6">
                      <h4 className="font-semibold text-slate-900 mb-2">Hotel Accommodation Information</h4>
                      <ul className="text-sm text-slate-600 mb-4 list-disc list-outside ml-5 space-y-1">
                        <li>Conference Registration &amp; Food (6 Meals): <strong>$75 per person</strong> for adults &amp; teens (children 0–12 free)</li>
                      </ul>
                      {hotelGuide ? (
                        <>
                          <div className="flex flex-wrap items-center gap-3">
                            <a
                              href={hotelGuide.viewHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={openHotelGuide(hotelGuide.viewHref, "view")}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm text-white shadow-md ring-2 ring-amber-300/60 hover:ring-amber-400 hover:scale-[1.02] active:scale-[0.99] transition-all bg-gradient-to-r from-sky-600 via-sky-500 to-amber-500"
                            >
                              <MapPin className="w-4 h-4" />
                              View Hotel Accommodation Guide
                            </a>
                            <a
                              href={hotelGuide.downloadHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              download="OFUESI-2026-Hotel-Accommodation-Guide.pdf"
                              onClick={openHotelGuide(hotelGuide.downloadHref, "download")}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm text-sky-700 bg-white border-2 border-sky-300 hover:bg-sky-50 hover:border-sky-400 transition-all shadow-sm"
                            >
                              <Download className="w-4 h-4" />
                              Download Hotel Accommodation Guide
                            </a>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Opens as a PDF in a new tab using your browser's native viewer.
                          </p>
                        </>
                      ) : (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                          Hotel Accommodation Guide will be available soon.
                        </div>
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* Live registration summary is rendered as a sticky sidebar (desktop)
                  and floating bottom card (mobile) — see <LiveRegistrationSummary /> below. */}




              {isSouthern && !isFreeStudent && (
              <>
                <RegistrationInfoRibbon />
                <div id="payment-section" className="scroll-mt-24">
                <Section title="6. Payment Options" subtitle="Pick how you'd like to pay. Your registration is saved either way — your regional treasurer will confirm payment once received.">



                <div className="space-y-4">
                  {/* Method picker */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { id: "zelle", title: "Zelle", desc: "Pay now via Zelle and upload your screenshot below." },
                      { id: "online", title: "Online (PayPal / Card)", desc: "Pay through the Give page, then upload your receipt screenshot below." },
                      { id: "in_person", title: "Cheque / In Person", desc: "Bring a cheque or pay in person at the conference. Optionally upload a cheque photo." },
                      { id: "pay_later", title: "Pay Later", desc: "Submit now, pay later. We'll send a reminder. Update your screenshot once paid." },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPaymentChoice(opt.id as any)}
                        className={`text-left rounded-lg border-2 p-4 transition ${
                          paymentChoice === opt.id
                            ? "border-sky-600 bg-sky-50 ring-2 ring-sky-200"
                            : "border-slate-200 bg-white hover:border-sky-300"
                        }`}
                      >
                        <p className="font-semibold text-slate-800">{opt.title}</p>
                        <p className="text-xs text-slate-600 mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>

                  {/* Method-specific instructions */}
                  {paymentChoice === "zelle" && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-sky-50 border border-sky-200 text-sm">
                          <p><strong>Payment Method:</strong> Zelle to OFUESI</p>
                          <p className="text-slate-700 mt-2"><strong>Zelle Recipient:</strong></p>
                          <p className="font-mono bg-white px-2 py-1 rounded inline-block mt-1 text-sky-700">{ZELLE_RECIPIENT}</p>
                          <p className="text-xs text-slate-500 mt-3">Recipient name: <strong>OFUESI Treasurer</strong></p>
                        </div>
                        <div className="p-4 rounded-lg bg-white border border-sky-200 flex flex-col items-center">
                          <img src={zelleQR} alt="Zelle QR code for OFUESI" className="w-40 h-40 object-contain rounded-md border border-border/50" loading="lazy" />
                          <p className="text-xs text-slate-500 mt-2">Scan to pay via Zelle</p>
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-50 border border-amber-300 text-sm">
                        <p className="font-semibold mb-1">Required — Zelle Memo Format</p>
                        <p className="text-slate-700">
                          <span className="font-mono bg-white px-2 py-1 rounded inline-block">[Region] Conference + Full Name</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Example: <em>South Conference + John Doe</em></p>
                        {memo && (
                          <p className="mt-2 text-emerald-700">Your memo: <span className="font-mono">{memo}</span></p>
                        )}
                      </div>
                    </>
                  )}

                  {paymentChoice === "online" && (
                    <div className="p-4 rounded-lg bg-sky-50 border border-sky-200 text-sm space-y-3">
                      <p className="font-semibold text-slate-800">Pay online via PayPal or Card</p>
                      <p className="text-slate-700">
                        Use the OFUESI Give page to pay <strong>${computedPrice}</strong> for{" "}
                        <em>{categoryLabel}</em>. Mention <strong>{memo || "[Region] Conference + Full Name"}</strong> in the note.
                      </p>
                      <Button asChild variant="outline" className="border-sky-400 text-sky-700">
                        <a href="/give" target="_blank" rel="noopener">Open Give page →</a>
                      </Button>
                      <p className="text-xs text-slate-600 italic">
                        After paying, upload your receipt screenshot below so your treasurer can match it.
                      </p>
                    </div>
                  )}

                  {paymentChoice === "in_person" && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm space-y-2">
                      <p className="font-semibold text-slate-800">Cheque or In-Person Payment</p>
                      <ol className="list-decimal list-inside text-slate-700 space-y-1.5">
                        <li>Make the cheque payable to <strong>OFUESI</strong>.</li>
                        <li>
                          On the memo line, write your <strong>full name</strong> and <strong>region</strong> — e.g.{" "}
                          <span className="font-mono bg-white px-1.5 py-0.5 rounded">
                            {memo || "[Region] Conference + Full Name"}
                          </span>
                          , so we can match the payment to your registration.
                        </li>
                        <li>Hand the cheque to your Regional Treasurer, or pay in person at the conference welcome desk on arrival.</li>
                      </ol>
                      <p className="text-xs text-slate-600 italic">
                        You can also upload a photo of the cheque below so we can pre-confirm your registration before the conference.
                      </p>
                    </div>
                  )}

                  {paymentChoice === "pay_later" && (
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm space-y-2">
                      <p className="font-semibold text-emerald-800">Pay later</p>
                      <p className="text-slate-700">
                        Your seat is reserved as <em>pending payment</em>. Come back to the{" "}
                        <strong>Payment Confirmation</strong> tab any time before the deadline to
                        pay via Zelle / online and upload your screenshot.
                      </p>
                    </div>
                  )}

                  {/* Optional screenshot upload — available for every paid method */}
                  {paymentChoice !== "pay_later" && (
                    <Field
                      label={
                        paymentChoice === "zelle" || paymentChoice === "online"
                          ? "Upload payment screenshot / receipt (REQUIRED — JPG, PNG, PDF · max 8MB)"
                          : "Upload payment screenshot / receipt (optional — JPG, PNG, PDF · max 8MB)"
                      }
                      error={errors.screenshot}
                    >
                      <label
                        htmlFor="payment-screenshot-input"
                        className="group inline-flex items-center gap-2 cursor-pointer select-none px-5 py-2.5 rounded-full font-semibold text-sm text-white shadow-lg ring-2 ring-amber-300/60 hover:ring-amber-400 hover:scale-[1.02] active:scale-[0.99] transition-all bg-gradient-to-r from-sky-600 via-sky-500 to-amber-500"
                      >
                        <Upload className="w-4 h-4" />
                        <span>{screenshot ? "Replace file" : "Choose file"}</span>
                      </label>
                      <input
                        id="payment-screenshot-input"
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
                        className="sr-only"
                      />
                      {screenshot ? (
                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[260px]">{screenshot.name}</span>
                          <button
                            type="button"
                            onClick={() => setScreenshot(null)}
                            className="ml-1 text-emerald-700/70 hover:text-emerald-900"
                            aria-label="Remove file"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 mt-2">No file chosen yet</p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {(paymentChoice === "zelle" || paymentChoice === "online")
                          ? "Required for Pay Now (Zelle / Online). It will be linked to your registration for treasurer review."
                          : "You can also upload it later from the Payment Confirmation tab."}
                      </p>
                    </Field>
                  )}

                  <p className="text-xs text-slate-600 italic">
                    Payment status is confirmed by your regional treasurer once the bank record matches.
                    You'll be notified when it's marked as paid.
                  </p>
                </div>
              </Section>
              </div>
              </>
              )}


              <Section
                title="General Comments / Special Requests"
                subtitle="Optional — share anything else we should know before your registration is reviewed."
              >
                <Textarea
                  rows={4}
                  maxLength={2000}
                  value={form.generalComments || ""}
                  onChange={(e) => set("generalComments", e.target.value)}
                  placeholder="Please share any comments, special requirements, scholarship request details, accommodation questions, or feedback about the registration process."
                />
              </Section>


              <Card className="border-amber-300 bg-amber-50/60 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Plane className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">General Travel Instructions / Guidelines</h3>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        Please plan ahead for your travel and stay. Bring your own toiletries,
                        bedsheets, blankets, pillows, and any regular medicines. A doctor/nurse will
                        be on-site for care as needed. Pray ahead that God speaks to each of us and
                        guides OFUESI toward His mission.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-sky-200 shadow-sm">
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="font-semibold text-slate-800">
                      {coordinatorRegion?.name?.toLowerCase() === "midwest" ? "Conference Coordinator" : "Regional Coordinator"}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {coordinatorRegion
                        ? coordinatorRegion.name?.toLowerCase() === "midwest"
                          ? `For more details or questions, please reach out to your ${coordinatorRegion.name} Region conference coordinator.`
                          : `For more details or questions, please reach out to your ${coordinatorRegion.name} Region regional coordinator.`
                        : "Contact your respective Regional Coordinator for more details or queries."}
                    </p>
                  </div>
                  {coordinatorRegion ? (
                    <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 to-amber-50 p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-white border border-sky-200 flex items-center justify-center text-sky-700 shadow-sm shrink-0">
                          <Users className="w-5 h-5" />
                        </div>
                        <div className="space-y-1.5">
                          <p className="font-semibold text-slate-900">
                            {coordinatorRegion.coordinator_name || (coordinatorRegion.name?.toLowerCase() === "midwest" ? "Conference Coordinator" : "Regional Coordinator")}
                          </p>
                          <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">
                            {coordinatorRegion.name} Region
                          </p>
                          {coordinatorRegion.coordinator_phone && (
                            <p className="text-sm text-slate-700 flex items-center gap-2">
                              <Phone className="w-4 h-4 text-sky-600" />
                              <a href={`tel:${coordinatorRegion.coordinator_phone}`} className="text-sky-700 hover:underline">
                                {coordinatorRegion.coordinator_phone}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Select your region above to see your coordinator's contact details.</p>
                  )}
                </CardContent>
              </Card>





              <div className="text-center pt-4 space-y-3">

                <div>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="bg-gradient-to-r from-sky-600 to-amber-500 hover:from-sky-700 hover:to-amber-600 text-white px-12 shadow-lg"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                    ) : (
                      <><Heart className="w-4 h-4 mr-2" /> Submit Registration</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
                  <Mail className="w-3 h-3" /> A confirmation email will be sent to your inbox.
                </p>
              </div>

            </form>
                  {isSouthern && !isFreeStudent && (
                    <LiveRegistrationSummary
                      form={{
                        category: form.category,
                        boardingType: form.boardingType,
                        includeSpouse: form.includeSpouse,
                        firstName: form.firstName,
                        lastName: form.lastName,
                        email: form.email,
                        mobile: form.mobile,
                        spouseFirstName: form.spouseFirst,
                        spouseLastName: form.spouseLast,
                        spouseEmail: form.spouseEmail,
                        spouseMobile: form.spouseMobile,

                        children: form.children as any,
                        otherAdults: form.otherAdults as any,
                        primaryAttendeeType: form.isStudent ? "student" : "adult",
                      }}
                      computedPrice={computedPrice}
                      familyBase={familyBase}
                      isHotel={isHotel}
                      hotelBillablePersons={hotelBillablePersons}
                      hotelFreeChildren={counts.c0_5 + counts.c6_12}
                      paymentChoice={paymentChoice}
                      paymentScrollTargetId="payment-section"
                      submitted={submitted}
                      submitting={submitting}
                    />
                  )}
                </div>
              </>
            )}

          </TabsContent>

          <TabsContent value="payment">
            <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
              <PaymentConfirmationPanel />
            </Suspense>
          </TabsContent>

          <TabsContent value="schedule">
            <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading schedule…</div>}>
              {isMidwest ? <MidwestProgramSchedule /> : <SouthernProgramSchedule />}
            </Suspense>
          </TabsContent>

          <TabsContent value="cancel">
            <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
              <CancelRegistrationPanel />
            </Suspense>
          </TabsContent>

          <TabsContent value="feedback">
            <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
              <ConferenceFeedbackPanel
                conferenceSlug="regional-conference-2026"
                conferenceYear={2026}
                regionName={selectedRegion?.name}
                regionStates={(selectedRegion as any)?.states || undefined}
              />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>

      {/* Duplicate registration modal */}
      <Dialog open={duplicateModal.open} onOpenChange={(o) => setDuplicateModal((p) => ({ ...p, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You're already registered</DialogTitle>
            <DialogDescription>
              We found an existing registration for <strong>{form.firstName} {form.lastName}</strong> using
              <strong> {form.email}</strong>. A confirmation email was already sent. You can cancel your previous
              registration if you need to start over with a new entry.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateModal({ open: false, id: null });
                setForm((p) => ({ ...p, email: "" }));
                toast({ title: "Use a different email to start a new registration." });
              }}
            >
              Use a different email
            </Button>
            <Button
              className="bg-gradient-to-r from-sky-600 to-amber-500 text-white"
              onClick={() => {
                setDuplicateModal({ open: false, id: null });
                setTab("cancel");
              }}
            >
              Cancel previous registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={errorModal.open} onOpenChange={(o) => setErrorModal((p) => ({ ...p, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <XCircle className="h-5 w-5" />
              {errorModal.title || "Please correct the following"}
            </DialogTitle>
            <DialogDescription>
              Fix the items below and submit again.
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 max-h-72 overflow-auto">
            {errorModal.items.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              className="bg-gradient-to-r from-sky-600 to-amber-500 text-white"
              onClick={() => setErrorModal((p) => ({ ...p, open: false }))}
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Additional-adult confirmation (Southern family only) */}
      <Dialog open={adultConfirmOpen} onOpenChange={(o) => setAdultConfirmOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Users className="h-5 w-5" />
              Additional Adult detected
            </DialogTitle>
            <DialogDescription>
              You have added {form.otherAdults.length === 1 ? "an" : `${form.otherAdults.length}`} Additional Adult{form.otherAdults.length === 1 ? "" : "s"} (18+). Each Additional Adult beyond husband and wife adds <strong>$125</strong> to your registration fee.
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 max-h-56 overflow-auto">
            {form.otherAdults.map((a) => (
              <li key={a.id}>
                {[a.first_name, a.last_name].filter(Boolean).join(" ").trim() || <em className="text-slate-400">(name pending)</em>}
              </li>
            ))}
          </ul>
          <div className="text-xs text-slate-500">If any of these are actually your spouse or a child under 18, click <strong>Review registration</strong> to fix it.</div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setAdultConfirmOpen(false)}
            >
              Review registration
            </Button>
            <Button
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white"
              onClick={() => {
                setAdultsConfirmed(true);
                setAdultConfirmOpen(false);
                // Re-run the submit flow now that the user has confirmed.
                setTimeout(() => {
                  const formEl = document.getElementById("conference-registration-form") as HTMLFormElement | null;
                  formEl?.requestSubmit();
                }, 0);
              }}
            >
              Yes, continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {selectedConference?.start && (
        <div className="container mx-auto px-3 sm:px-4 pb-8 sm:pb-12 max-w-5xl">
          <CountdownTimer
            targetDate={selectedConference.start}
            startTime={selectedConference.startTime}
            timezone={selectedConference.timezone}
            label={`${selectedConference.displayRegionName} conference begins in`}
          />

        </div>
      )}
    </div>
  );
};


const InfoCard = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl shadow-md border border-sky-100 p-6 md:p-8">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
        {icon}
      </div>
      <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
    </div>
    {children}
  </div>
);

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.4 }}
    className="bg-white rounded-2xl shadow-md border border-sky-100 p-6 md:p-8"
  >
    <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-1">{title}</h2>
    {subtitle && <p className="text-sm text-slate-500 mb-5">{subtitle}</p>}
    {children}
  </motion.section>
);

const Field = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive mt-1">{error}</p>}
  </div>
);

const CountField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onChange(Math.max(0, value - 1))}
      >
        −
      </Button>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
        className="text-center"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onChange(value + 1)}
      >
        +
      </Button>
    </div>
  </div>
);

const ReceiptSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-sm font-semibold uppercase tracking-wide text-sky-700 mb-2">{title}</h3>
    <div className="rounded-lg border border-sky-100 bg-sky-50/30 divide-y divide-sky-100">
      {children}
    </div>
  </div>
);

const ReceiptRow = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) => (
  <div className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={highlight ? "font-bold text-sky-700 text-base" : "font-medium text-slate-800 text-right"}>
      {value}
    </span>
  </div>
);

const RegionDetailsPanel = ({
  region,
  flyer,
  venues,
  program,
  worship,
  conference,
  onBack,
  onRegister,
}: {
  region: { id: string; name: string };
  flyer?: ContentRow;
  venues: ContentRow[];
  program: ContentRow[];
  worship: ContentRow[];
  conference: ResolvedPublicConference | null;
  onBack: () => void;
  onRegister: () => void;
}) => (
  <div className="space-y-6">
    {(() => {
      return (
        <>

    <div className="relative flex items-center justify-center">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-0 inline-flex items-center gap-1 text-sm text-sky-700 hover:text-amber-600"
      >
        <ArrowLeft className="w-4 h-4" /> All regions
      </button>
      <Button
        onClick={onRegister}
        size="lg"
        className="bg-gradient-to-r from-sky-600 to-amber-500 text-white shadow-md"
      >
        <Heart className="w-4 h-4 mr-2" /> Register for {region.name}
      </Button>
    </div>

    {/* Flyer */}
    <InfoCard icon={<ImageIcon className="w-6 h-6 text-amber-600" />} title={`${region.name} — Conference Details`}>
      {(conference?.flyer || flyer?.data?.image_url) ? (
        <FlyerWithDownload
          src={conference?.flyer || flyer?.data?.image_url}
          regionName={region.name}
        />
      ) : null}
      {flyer?.body && <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{flyer.body}</p>}
      {/* No floating QR panel — the flyer artwork above already contains
          the region's own QR inside its existing placeholder. */}
    </InfoCard>



    {/* Venue with map + directions */}
    <InfoCard icon={<MapPin className="w-6 h-6 text-amber-600" />} title="Venue & Directions">
      {conference?.venue ? (
        <div className="space-y-4">
          <VenueMap
            name={conference.venueName || region.name}
            address={[conference.venueAddress, conference.cityState].filter(Boolean).join(", ") || conference.venue || ""}
          />
          <div className="rounded-lg border border-sky-100 bg-white p-4 text-sm text-slate-700 space-y-2">
            <p className="font-semibold text-slate-900">{conference.venueName || "Venue"}</p>
            {conference.venueAddress && <p>{conference.venueAddress}</p>}
            {conference.cityState && <p>{conference.cityState}</p>}
            {conference.contactPhone && <p><span className="font-semibold">Mobile:</span> {conference.contactPhone}</p>}
          </div>
        </div>
      ) : venues.length === 0 ? (
        <p className="text-slate-500">Venue details coming soon.</p>
      ) : (
        <div className="space-y-5">
          {venues.map((v) => {
            const address = [v.data?.address_line1, v.data?.address_line2]
              .filter(Boolean)
              .join(", ");
            return (
              <div key={v.id} className="space-y-3">
                {v.subtitle && (
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {v.subtitle}
                  </span>
                )}
                {address ? (
                  <VenueMap
                    name={v.title || undefined}
                    address={address}
                    directions={v.data?.directions || undefined}
                  />
                ) : (
                  <p className="font-semibold text-slate-800">{v.title || "Venue TBA"}</p>
                )}
                {(v.data?.check_in || v.data?.parking) && (
                  <div className="text-xs text-slate-500 space-y-1">
                    {v.data?.check_in && <p><strong>Check-in:</strong> {v.data.check_in}</p>}
                    {v.data?.parking && <p><strong>Parking:</strong> {v.data.parking}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </InfoCard>
        </>
      );
    })()}


    {/* Program schedule */}
    <InfoCard icon={<CalendarDays className="w-6 h-6 text-amber-600" />} title="Program Schedule">
      {/midwest/i.test(region.name) ? (
        <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading schedule…</div>}>
          <MidwestProgramSchedule />
        </Suspense>
      ) : /southern/i.test(region.name) ? (
        <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading schedule…</div>}>
          <SouthernProgramSchedule />
        </Suspense>
      ) : program.length === 0 ? (
        <p className="text-slate-500">Schedule coming soon.</p>
      ) : (
        <div className="space-y-4">
          {program.map((p) => (
            <div key={p.id} className="rounded-lg border border-sky-100 bg-white p-4">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-semibold text-sky-700">{p.title}</h3>
                {p.subtitle && <span className="text-xs text-slate-500">{p.subtitle}</span>}
              </div>
              {p.body && <p className="text-sm text-slate-700 whitespace-pre-line">{p.body}</p>}
              {Array.isArray(p.data?.items) && p.data.items.length > 0 && (
                <ul className="mt-2 text-sm text-slate-700 space-y-1">
                  {p.data.items.map((it: string, idx: number) => (
                    <li key={idx} className="flex gap-2"><span className="text-amber-600">•</span><span>{it}</span></li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </InfoCard>

    {/* Worship */}
    <InfoCard icon={<Music className="w-6 h-6 text-amber-600" />} title="Worship Details">
      <div className="space-y-4">
        {/southern/i.test(region.name) && (
          <Suspense fallback={<div className="rounded-xl border border-sky-100 bg-sky-50/40 p-6 text-center text-sm text-slate-500">Loading theme song…</div>}>
            <ThemeSongCard />
          </Suspense>
        )}
        {worship.length === 0 ? (
          !/southern/i.test(region.name) && <p className="text-slate-500">Worship details coming soon.</p>
        ) : (
          <div className="space-y-3">
            {worship.map((w) => (
              <div key={w.id} className="rounded-lg border border-sky-100 bg-sky-50/40 p-4">
                <h3 className="font-semibold text-sky-700">{w.title}</h3>
                {w.subtitle && <p className="text-xs text-amber-700 mt-1">{w.subtitle}</p>}
                {w.body && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{w.body}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </InfoCard>

    <div className="text-center pt-2">
      <Button
        onClick={onRegister}
        size="lg"
        className="bg-gradient-to-r from-sky-600 to-amber-500 text-white px-10 shadow-lg"
      >
        <Heart className="w-4 h-4 mr-2" /> Continue to Registration Form
      </Button>
    </div>
  </div>
);

const ageGroupLabel = (g: string) =>
  g === "adult" ? "Adult" :
  g === "teen" ? "Teen 13–17" :
  g === "child_6_12" ? "Child 6–12" :
  g === "child_0_5" ? "Child 0–5" : g;

const SummaryStat = ({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) => (
  <div className={`rounded-md p-2 border ${highlight ? "bg-sky-100 border-sky-300" : "bg-white border-sky-100"}`}>
    <div className={`text-base font-bold ${highlight ? "text-sky-800" : "text-slate-800"}`}>{value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </div>
);

const FlyerWithDownload = ({ src, regionName }: { src: string; regionName: string }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const filename = `OFUESI-${regionName.replace(/\s+/g, "-")}-Conference-2026-Flyer.jpg`;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await fetch(src, { mode: "cors" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab so user can long-press / right-click save
      window.open(src, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative w-full max-w-[560px] overflow-hidden rounded-lg border border-sky-100 bg-white shadow-sm transition hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-sky-400"
          aria-label={`View and download ${regionName} flyer`}
        >
          <img src={src} alt={`${regionName} flyer`} className="w-full block" />
          <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="m-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow">
              <Upload className="w-3.5 h-3.5 rotate-180" /> Tap to save or download
            </span>
          </div>
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{regionName} — Conference Flyer</DialogTitle>
            <DialogDescription>
              Save this flyer as an image, download the file, or open it in a new tab to share.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto rounded-md border border-sky-100 bg-white">
            <img src={src} alt={`${regionName} flyer preview`} className="w-full block" />
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
              className="w-full sm:w-auto"
            >
              <ImageIcon className="w-4 h-4 mr-2" /> Open Image
            </Button>
            <Button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="w-full sm:w-auto bg-gradient-to-r from-sky-600 to-amber-500 text-white"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2 rotate-180" /> Download Flyer</>
              )}
            </Button>
          </DialogFooter>
          <p className="text-xs text-slate-500 text-center sm:text-left">
            On mobile, you can also long-press the image above and choose "Save to Photos".
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConferenceRegistration;


